import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { fetchInChunks } from "../utils/supabaseChunkedIn";
import { getCachedSignedUrl, setCachedSignedUrl } from '../utils/imageCache';
import { resolveMarcaActivaId } from '../utils/marcaActiva';
import {
  postImageEdit,
  buildEdicionesPrevias,
  MODO_UTILIZADO_EDICION,
  type ImageEditBody,
  type OrigenMascara,
} from '../lib/imageEdit';
import {
  maskDataUriAB64,
  maskDesdeTrazo,
  type PuntoImagen,
  requestMascaraBorrado,
  unirMascaras,
} from '../lib/borrarObjeto';
// `ArrowLeft` viene de `principal` (el back del encabezado); `Camera` y
// `AngleSelector3D` se van con el modo "Cambiar ángulo", que esta rama eliminó.
import { Undo2, Redo2, Save, Loader2, Check, Plus, Pencil, MousePointer2, Eraser, RefreshCw, Hand, X, ImageIcon, ChevronDown, ChevronLeft, ArrowLeft, Copy } from 'lucide-react';
import CachedImg from '../components/CachedImg';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { SendCreditsButton } from '../components/SendCreditsButton';
import { useSidebar } from '../context/SidebarContext';
import { MARCO_PANTALLA, BORDE_PANTALLA } from '../components/PantallaChrome';

/** Producto del picker de "Reemplazar producto".
 *  `imageKey` es el valor crudo de `productos_imagenes.url`: o un nombre de
 *  archivo dentro del bucket `productos` (hay que firmarlo) o una URL http
 *  completa (se usa tal cual). NO guarda la signed URL: esas viven en el mapa
 *  `productThumbs`, que se llena a medida que las cards entran en viewport. */
interface ProductVariant {
  id: string;
  nombre: string;
  imageKey: string | null;
}

/** Ancestro scrolleable más cercano. El grid del picker vive dentro de un
 *  `overflow-y-auto` (el modal de desktop y el sheet de mobile), y con
 *  `root: null` el `rootMargin` NO atraviesa ese clip: el adelanto de 200px se
 *  perdería y las miniaturas cargarían justo al aparecer. */
function nearestScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement || null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Preferencia de "selección asistida" del borrado. Se recuerda entre sesiones a
 * propósito: quien la apaga es porque la detección no le está funcionando bien en SUS
 * imágenes, y volver a encontrarla prendida en cada visita sería pelear con la app.
 */
const CLAVE_SELECCION_ASISTIDA = 'atlas:borrado:seleccion-asistida';

/**
 * Caja donde el `<img class="object-contain">` dibuja realmente la imagen, en píxeles
 * INTERNOS del canvas que lo cubre.
 *
 * No siempre es el canvas entero: cuando la caja del `<img>` no tiene el mismo aspecto
 * que la imagen (pasa en desktop con una imagen muy ancha y la ventana angosta, donde
 * el `maxWidth` recorta la caja pero el alto queda fijo), `object-contain` deja banda y
 * la imagen queda centrada adentro. Mapear contra el canvas entero en ese caso manda a
 * SAM coordenadas corridas — y en un modo que se trata justamente de precisión, eso es
 * la diferencia entre borrar el objeto y borrar el fondo de al lado.
 */
function cajaContenido(
  canvas: HTMLCanvasElement,
  dims: { width: number; height: number },
): { x: number; y: number; w: number; h: number } {
  const escala = Math.min(canvas.width / dims.width, canvas.height / dims.height);
  const w = dims.width * escala;
  const h = dims.height * escala;
  return { x: (canvas.width - w) / 2, y: (canvas.height - h) / 2, w, h };
}

/** Miniatura de una card del picker. Pide la signed URL (vía `onNeedsUrl`) sólo
 *  cuando la card está a menos de 200px del viewport, y la imagen se baja por
 *  `CachedImg` (caché de blobs + su propio lazy).
 *
 *  Antes el catálogo entero se firmaba de una y EN SERIE dentro de
 *  `fetchProducts`, con el grid entero detrás del skeleton hasta la última
 *  respuesta: N–2N round-trips secuenciales antes de mostrar nada. Ver
 *  ADR-2026-08-11-reemplazar-producto-firma-lazy. */
const ProductPickerThumb: React.FC<{
  variantId: string;
  imageKey: string | null;
  nombre: string;
  /** Entrada de `productThumbs`: `undefined` = todavía no se pidió,
   *  `null` = se pidió y no hay archivo, string = signed URL lista. */
  thumb: string | null | undefined;
  onNeedsUrl: (variantId: string, imageKey: string) => void;
  children?: React.ReactNode;
}> = ({ variantId, imageKey, nombre, thumb, onNeedsUrl, children }) => {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const askedRef = useRef<boolean>(false);
  const isHttp = !!imageKey && imageKey.startsWith('http');
  const alreadyTried = thumb !== undefined;
  const resolved = isHttp ? imageKey : thumb || null;

  useEffect(() => {
    // Nada que firmar: sin imagen, ya es http, o ya se intentó.
    if (!imageKey || isHttp || alreadyTried) return;
    const ask = () => {
      if (askedRef.current) return;
      askedRef.current = true;
      onNeedsUrl(variantId, imageKey);
    };
    const el = holderRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      ask();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          ask();
        });
      },
      { root: nearestScrollParent(el), rootMargin: '200px 0px', threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [variantId, imageKey, isHttp, alreadyTried, onNeedsUrl]);

  return (
    <div ref={holderRef} className="relative">
      {resolved ? (
        <CachedImg
          src={resolved}
          alt={nombre}
          className="w-full aspect-square object-cover"
        />
      ) : imageKey && !alreadyTried ? (
        // Tiene imagen pero todavía no está firmada: skeleton, no ícono de
        // "sin imagen" (que leería como producto sin foto).
        <div className="w-full aspect-square bg-neutral-100 animate-pulse" />
      ) : (
        // Sin imagen, o la firma falló: placeholder definitivo. Sin este caso
        // un archivo faltante en storage dejaba el skeleton pulsando para
        // siempre, porque el intento no se repite.
        <div className="w-full aspect-square bg-neutral-100 flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-neutral-400" />
        </div>
      )}
      {children}
    </div>
  );
};

export const EditarImagen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const imageIdParam = (params.get('id') || '').trim();
  const companyId = (params.get('companyId') || '').trim();
  // imagenes_generadas.id es UUID (string)
  const imageId: string = imageIdParam;

  const [loading, setLoading] = useState<boolean>(true);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState<string>('');
  // Historial de la sesión de edición. `instruction` es el texto que PRODUJO esa
  // entrada: la primera (imagen original) no tiene, y el cambio de producto tampoco
  // (no hay texto). Se usa para mandar el contexto de las ediciones previas.
  // Ver ADR-2026-08-12-contexto-de-ediciones-previas.
  const [history, setHistory] = useState<{ id: string; url: string; instruction?: string }[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showEditSuccess, setShowEditSuccess] = useState<boolean>(false);
  const [isWaitingForImageLoad, setIsWaitingForImageLoad] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [showEditModeMenu, setShowEditModeMenu] = useState<boolean>(false);
  const [showSaveMenu, setShowSaveMenu] = useState<boolean>(false);
  // `precise` sigue existiendo y funcionando, pero se OCULTÓ de la tab bar el
  // 2026-08-13 (pedido de Matías): el modo nuevo `object_erase` ocupa su lugar.
  // Se dejó el código para poder volver a prenderlo cambiando la lista de tabs.
  const [editMode, setEditMode] = useState<'instruction' | 'precise' | 'object_erase' | 'product_change'>('instruction');
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const [showModerationError, setShowModerationError] = useState<boolean>(false);
  // Sugerencia de regenerar: el backend clasificó la instrucción como un pedido que
  // la edición no puede cumplir y cortó ANTES de generar (no se cobró crédito).
  // `parcial` = parte del pedido sí era editable, y es lo único que varía el copy.
  // El `motivo` de la respuesta NO se guarda: el aviso es uno solo para los tres
  // casos, y la calibración se hace con los logs del backend (que sí lo registran).
  // Ver ADR-2026-08-12-clasificador-sugerir-regenerar.
  const [regenerarSugerido, setRegenerarSugerido] = useState<{ parcial: boolean } | null>(null);
  const [showHelpPopup, setShowHelpPopup] = useState<boolean>(false);
  const [userRubro, setUserRubro] = useState<string>('Moda e Indumentaria');
  const desktopInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Precise edit mode state
  const [hasMask, setHasMask] = useState<boolean>(false);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasMobileRef = useRef<HTMLCanvasElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageMobileRef = useRef<HTMLImageElement>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  // ── Modo "Borrar objeto" (SAM 3 + Bria) — ADR-2026-08-13-modo-borrar-objeto-gesto-simple ──
  // UN gesto y listo, sin puntos que sumar ni opciones que comparar: un toque marca lo
  // que hay ahí, y un arrastre marca lo que se pinta. Reusa los canvas del modo preciso.
  /** Máscara vigente (data-URI PNG): la de SAM, o la del propio trazo si SAM no encontró nada. */
  const [borradoMask, setBorradoMask] = useState<string | null>(null);
  const [borradoStatus, setBorradoStatus] = useState<'idle' | 'detectando' | 'listo' | 'error'>('idle');
  /**
   * De dónde salió la máscara acumulada. Viaja en el request porque Bria trata distinto
   * un contorno de SAM (`automatic`) de uno pintado a pulso (`manual`), y como la
   * selección es aditiva puede terminar siendo `mixta`.
   */
  const [borradoOrigen, setBorradoOrigen] = useState<OrigenMascara | null>(null);
  const [borradoError, setBorradoError] = useState<string | null>(null);
  /** Descarta respuestas viejas: SAM tarda ~1-2s y el usuario puede volver a marcar antes. */
  const maskReqIdRef = useRef<number>(0);
  /** Mismo problema pero para el dibujo: la máscara se carga como Image (async) y un
   *  dibujo viejo no debe pisar al nuevo. */
  const borradoDrawRef = useRef<number>(0);
  /**
   * Gesto actual en píxeles de la imagen REAL (1 punto = toque, N = arrastre).
   *
   * Vive en un ref y NO en el state a propósito: un arrastre dispara ~60 `pointermove`
   * por segundo, y un `setState` por evento re-renderiza todo el editor (que es grande)
   * en cada uno. El trazo se dibuja imperativamente sobre el canvas, así que React no
   * necesita enterarse.
   */
  const trazoRef = useRef<PuntoImagen[]>([]);
  const trazandoRef = useRef<boolean>(false);
  /** 0..1, lo mueve el rAF del pulso mientras SAM está detectando. */
  const pulsoRef = useRef<number>(0);
  /**
   * Overlay rojo ya procesado de la máscara acumulada, cacheado.
   *
   * Como la selección es aditiva, durante un arrastre hay que dibujar la máscara que ya
   * estaba MÁS el trazo en curso, ~60 veces por segundo. Procesar el PNG de la máscara en
   * cada frame es inviable, así que se procesa una vez cada vez que la máscara cambia.
   */
  const overlayRef = useRef<{ src: string; canvas: HTMLCanvasElement } | null>(null);
  /**
   * Con la asistencia PRENDIDA, el gesto se le manda a SAM y se selecciona el objeto que
   * hay ahí. APAGADA, la máscara es literalmente lo que se pinta — sin red, sin espera y
   * sin sorpresas. Es la válvula de escape para cuando la detección no acierta.
   */
  const [seleccionAsistida, setSeleccionAsistida] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CLAVE_SELECCION_ASISTIDA) !== 'off';
    } catch {
      return true; // modo privado / storage bloqueado
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_SELECCION_ASISTIDA, seleccionAsistida ? 'on' : 'off');
    } catch {
      /* no poder recordar la preferencia no puede romper el modo */
    }
  }, [seleccionAsistida]);
  // Ratio (w/h) que llega desde resultados por navigation-state → reservamos la
  // caja de la imagen desde el PRIMER frame, antes de que decodifique, para que
  // no aparezca chica y "salte". `imageDimensions` (real) lo reemplaza al cargar;
  // es el mismo ratio, así que el cambio es invisible.
  const initialAspect = useMemo(() => {
    const a = (location.state as { targetAspect?: number } | null)?.targetAspect;
    return typeof a === 'number' && a > 0 ? a : undefined;
  }, [location.state]);

  // Product change mode state
  const [showProductPicker, setShowProductPicker] = useState<boolean>(false);
  // Animación slide-up del picker en mobile: `productPickerAnimated` se
  // activa con rAF al montar y se apaga al cerrar, dejando que la
  // transición CSS (translateY 100% → 0%) corra. `productPickerDragY` y
  // `productPickerDragStartY` manejan el swipe-down-to-close.
  const [productPickerAnimated, setProductPickerAnimated] =
    useState<boolean>(false);
  const [productPickerDragY, setProductPickerDragY] = useState<number>(0);
  const productPickerDragStartY = useRef<number | null>(null);
  const [products, setProducts] = useState<ProductVariant[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductVariant | null>(null);
  // variante_id → signed URL de su miniatura. Se llena de a poco, on demand.
  // Clave presente con `null` = ya se intentó y no hay archivo (no se reintenta).
  const [productThumbs, setProductThumbs] = useState<Record<string, string | null>>({});
  // Variantes con una firma en vuelo: evita pedirla dos veces cuando la misma
  // card está montada en los dos grids (el de desktop y el sheet de mobile).
  const signingThumbsRef = useRef<Set<string>>(new Set());

  // Product picker slide-up helpers (mobile). Mismo patrón que los menús
  // de `PruebaNuevaInterfaz`: rAF para activar la entrada, setTimeout en
  // el cierre para que la transición CSS corra antes de desmontar, y
  // touch handlers para el gesto de "deslizar hacia abajo para cerrar".
  useEffect(() => {
    if (showProductPicker) {
      requestAnimationFrame(() => setProductPickerAnimated(true));
    }
  }, [showProductPicker]);
  const closeProductPicker = () => {
    setProductPickerAnimated(false);
    setProductPickerDragY(0);
    productPickerDragStartY.current = null;
    setTimeout(() => setShowProductPicker(false), 300);
  };
  const handleProductPickerTouchStart = (e: React.TouchEvent) => {
    productPickerDragStartY.current = e.touches[0].clientY;
  };
  const handleProductPickerTouchMove = (e: React.TouchEvent) => {
    if (productPickerDragStartY.current === null) return;
    e.preventDefault();
    const deltaY = e.touches[0].clientY - productPickerDragStartY.current;
    if (deltaY > 0) setProductPickerDragY(deltaY);
  };
  const handleProductPickerTouchEnd = () => {
    if (productPickerDragY > 100) {
      closeProductPicker();
    } else {
      setProductPickerDragY(0);
      productPickerDragStartY.current = null;
    }
  };

  // Enable Save only when the current image differs from the original (i.e., after at least one edit and not undone)
  const hasEdits = currentIndex > 0;

  // Prevent body scroll on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const checkSubscriptionLimits = async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', profile.id_company)
        .single();

      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true;
    }
  };

  // Fetch user's rubro on mount
  useEffect(() => {
    const fetchRubro = async () => {
      if (!companyId) return;
      try {
        const { data: company } = await supabase
          .from('company_profiles')
          .select('rubro')
          .eq('id_company', companyId)
          .single();

        if (company?.rubro) {
          setUserRubro(company.rubro as string);
        }
      } catch (err) {
        // Keep default rubro on error
      }
    };
    fetchRubro();
  }, [companyId]);

  useEffect(() => {
    const fetchImage = async () => {
      if (!imageIdParam || !companyId) {
        setLoading(false);
        setError('No se encontró información de la imagen.');
        return;
      }
      try {
        // Preview instantáneo (Slow 4G): si venimos de resultados, la imagen YA
        // se descargó y viene en `cachedImages` del navigation-state. La
        // mostramos al toque mientras firmamos+bajamos la full en segundo plano,
        // en vez de quedarnos en el loading hasta que termine la descarga full.
        // Editar usa `imagen_id` (no la URL) → el preview es SOLO visual, no
        // afecta la edición.
        const cachedList = (
          location.state as {
            cachedImages?: { id: unknown; signedUrl?: string }[];
          } | null
        )?.cachedImages;
        const cachedEntry = Array.isArray(cachedList)
          ? cachedList.find((i) => String(i.id) === imageIdParam)
          : undefined;
        if (cachedEntry?.signedUrl) {
          setImageUrl(cachedEntry.signedUrl);
          setLoading(false); // ya mostramos el preview; la full entra al swap
        } else {
          setLoading(true); // sin preview cacheado → spinner como antes
        }
        setError(null);
        // Buscar por id (UUID string); fallback a file_name exacto o prefijo
        let imageRow: { id: string; file_name: string } | null = null;
        const { data: byId, error: byIdErr } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('id', imageIdParam)
          .eq('id_company', companyId)
          .maybeSingle();
        if (byId && !byIdErr) imageRow = byId as any;
        if (!imageRow) {
          const { data: exactData } = await supabase
            .from('imagenes_generadas')
            .select('id, file_name')
            .eq('file_name', imageIdParam)
            .eq('id_company', companyId)
            .maybeSingle();
          if (exactData) imageRow = exactData as any;
        }
        if (!imageRow) {
          const { data: likeData } = await supabase
            .from('imagenes_generadas')
            .select('id, file_name')
            .like('file_name', `${imageIdParam}.%`)
            .eq('id_company', companyId)
            .maybeSingle();
          if (likeData) imageRow = likeData as any;
        }
        if (!imageRow) throw new Error('Imagen no disponible');
        if (!imageRow?.file_name) throw new Error('Imagen no disponible');

        const filePath = `${companyId}/full/${imageRow.file_name}`;
        const { data: signed } = await supabase.storage
          .from('generadas')
          .createSignedUrl(filePath, 3600);
        if (signed?.signedUrl) {
          setImageUrl(signed.signedUrl);
          setHistory([{ id: imageRow.id as string, url: signed.signedUrl }]);
          setCurrentIndex(0);
        } else {
          throw new Error('No se pudo obtener la imagen');
        }
      } catch (e: any) {
        console.error('Error cargando imagen para edición:', e);
        setError('Error al cargar la imagen para edición.');
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, [imageId, companyId]);

  // Keep imageUrl in sync with the current history index
  useEffect(() => {
    if (currentIndex >= 0 && history[currentIndex]) {
      setImageUrl(history[currentIndex].url);
    }
  }, [currentIndex, history]);

  // Auto-hide the success chip a few seconds after a successful edit
  useEffect(() => {
    if (!showEditSuccess) return;
    const t = window.setTimeout(() => setShowEditSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [showEditSuccess]);

  // Helper: auto-resize a textarea up to 3 lines and then make it scrollable
  const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const baseHeight = 44; // px: altura base equivalente al input original (h-11)
    const text = el.value || '';

    // Sin texto: altura base fija, sin scroll
    if (!text) {
      el.style.height = `${baseHeight}px`;
      el.style.overflowY = 'hidden';
      return;
    }

    // Disable transition during measurement so 'auto' doesn't break animation
    el.style.transition = 'none';
    el.style.height = 'auto';
    const scroll = el.scrollHeight;

    // Umbrales discretos: 1 línea, 2 líneas o 3 líneas máximo
    const oneLineThreshold = baseHeight + 4; // pequeña tolerancia
    const twoLineThreshold = baseHeight * 2 + 4;

    let lines = 1;
    if (scroll > twoLineThreshold) {
      lines = 3;
    } else if (scroll > oneLineThreshold) {
      lines = 2;
    }

    const targetHeight = baseHeight * lines;
    // Force layout read, then re-enable transition for smooth animation
    el.getBoundingClientRect();
    el.style.transition = 'height 200ms ease';
    el.style.height = `${targetHeight}px`;
    el.style.overflowY = lines === 3 && scroll > targetHeight ? 'auto' : 'hidden';
  };

  // Fetch products for product change mode.
  // Sólo lee la base (variantes + su imagen principal) y pinta el grid: las
  // signed URLs de storage NO se piden acá, las pide cada card al entrar en
  // viewport vía `ensureProductThumb`. Ver
  // ADR-2026-08-11-reemplazar-producto-firma-lazy.
  const fetchProducts = async () => {
    if (!companyId) return;
    setLoadingProducts(true);
    try {
      // Filtro por marca activa, igual que `SeleccionarProductosAnuncio`: sin
      // esto el picker mostraba el catálogo entero de la empresa y en cuentas
      // multi-marca ofrecía productos que la pantalla de selección no lista.
      // Ver ADR-2026-08-11-reemplazar-producto-filtra-marca-activa.
      const brandIdFilter = await resolveMarcaActivaId(companyId);

      // Con marca activa filtramos por `productos.brand_id` vía embed
      // inner-join, incluyendo SIEMPRE los productos sin marca
      // (`brand_id IS NULL`): son de la empresa y nunca se asignaron a una
      // marca, no deben quedar ocultos. Ver ADR-018.
      // El select va en una variable tipada `string` a propósito: pasarle un
      // ternario de literales al `.select()` hace fallar al parser de tipos de
      // supabase-js (`ParserError`, no acepta la unión) y el resultado se
      // castea abajo.
      const selectCols: string = brandIdFilter
        ? 'id, nombre, productos!inner(brand_id)'
        : 'id, nombre';

      let queryBuilder = supabase
        .from('variantes')
        .select(selectCols)
        .eq('activo', true)
        .eq('id_company', companyId);

      if (brandIdFilter) {
        queryBuilder = queryBuilder.or(
          `brand_id.eq.${brandIdFilter},brand_id.is.null`,
          { referencedTable: 'productos' },
        );
      }

      const { data: variantsRaw, error: variantsError } = await queryBuilder
        .order('created_at', { ascending: false });

      if (variantsError) throw variantsError;

      const variantsData = (variantsRaw ?? []) as unknown as Array<{ id: string; nombre: string }>;

      if (variantsData.length === 0) {
        setProducts([]);
        setSelectedProduct(null);
        return;
      }

      const variantIds = variantsData.map(v => v.id);

      // Fetch principal images for each variant
      // Fetch en lotes: con catálogos grandes la lista de variante_id en la URL
      // excede el límite del gateway REST de Supabase (HTTP 400). Ver
      // utils/supabaseChunkedIn.
      const imagesData = await fetchInChunks<any>(variantIds, (chunk) =>
        supabase
          .from('productos_imagenes')
          .select('url, variante_id')
          .eq('id_company', companyId)
          .eq('principal', true)
          .in('variante_id', chunk),
      );

      const keyByVariant: Record<string, string> = {};
      for (const img of (imagesData || [])) {
        const variantId = img.variante_id as string;
        if (!keyByVariant[variantId]) keyByVariant[variantId] = img.url as string;
      }

      // Build products array
      const productsList: ProductVariant[] = variantsData.map(v => ({
        id: v.id,
        nombre: v.nombre,
        imageKey: keyByVariant[v.id] || null
      }));

      setProducts(productsList);
      // Al cambiar de marca el producto elegido puede ya no estar en el
      // catálogo filtrado: hay que soltarlo, porque el submit manda su
      // `variante_id` y sería el de otra marca.
      setSelectedProduct(prev =>
        prev && !productsList.some(p => p.id === prev.id) ? null : prev,
      );
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  /** Firma la miniatura de una variante. Prueba primero el thumbnail `opt/` y
   *  cae al `full/` si no existe (el `opt/` se genera best-effort en la subida,
   *  así que hay productos viejos sin él). Reusa la caché de firmas de
   *  `utils/imageCache`, compartida con las demás pantallas: si ya se vio el
   *  producto en Seleccionar productos, no hay request. */
  const ensureProductThumb = useCallback(async (variantId: string, imageKey: string) => {
    if (!companyId || !imageKey) return;
    const optPath = `${companyId}/${variantId}/opt/${imageKey}`;
    const fullPath = `${companyId}/${variantId}/full/${imageKey}`;

    const cached = getCachedSignedUrl(optPath) || getCachedSignedUrl(fullPath);
    if (cached) {
      setProductThumbs(prev => (prev[variantId] === cached ? prev : { ...prev, [variantId]: cached }));
      return;
    }
    if (signingThumbsRef.current.has(variantId)) return;
    signingThumbsRef.current.add(variantId);

    try {
      const { data: optData } = await supabase.storage
        .from('productos')
        .createSignedUrl(optPath, 3600);

      let signedPath = optPath;
      let signedUrl = optData?.signedUrl || null;

      if (!signedUrl) {
        const { data: fullData } = await supabase.storage
          .from('productos')
          .createSignedUrl(fullPath, 3600);
        signedPath = fullPath;
        signedUrl = fullData?.signedUrl || null;
      }

      if (signedUrl) setCachedSignedUrl(signedPath, signedUrl);
      // Se escribe la clave también cuando no hay URL: marca "ya se intentó" y
      // la card pasa al placeholder en vez de quedarse en el skeleton.
      setProductThumbs(prev => ({ ...prev, [variantId]: signedUrl }));
    } catch (err) {
      console.error('Error firmando la miniatura del producto:', err);
      setProductThumbs(prev => ({ ...prev, [variantId]: null }));
    } finally {
      signingThumbsRef.current.delete(variantId);
    }
  }, [companyId]);

  /** Miniatura resuelta de un producto (para el chip del producto elegido). */
  const productThumbUrl = (product: ProductVariant): string | null => {
    if (!product.imageKey) return null;
    if (product.imageKey.startsWith('http')) return product.imageKey;
    return productThumbs[product.id] || null;
  };

  // Load products when entering product_change mode
  useEffect(() => {
    if (editMode === 'product_change' && products.length === 0) {
      fetchProducts();
    }
  }, [editMode]);

  // El catálogo del picker está filtrado por marca activa, así que si la marca
  // cambia desde el sidebar (u otra pantalla) mientras estamos acá hay que
  // volver a leerlo. `fetchProducts` suelta el producto elegido si dejó de
  // estar disponible. Mismo evento que escuchan `useBrandProfile` y
  // `SeleccionarProductosAnuncio`.
  useEffect(() => {
    if (editMode !== 'product_change') return;
    const onBrandChanged = () => { void fetchProducts(); };
    window.addEventListener('atlas:brand-changed', onBrandChanged);
    return () => window.removeEventListener('atlas:brand-changed', onBrandChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, companyId]);

  // Clear mask when switching away from precise mode
  useEffect(() => {
    if (editMode !== 'precise') {
      setHasMask(false);
      // Clear both canvases
      const canvasDesktop = maskCanvasRef.current;
      const canvasMobile = maskCanvasMobileRef.current;
      if (canvasDesktop) {
        const ctx = canvasDesktop.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasDesktop.width, canvasDesktop.height);
      }
      if (canvasMobile) {
        const ctx = canvasMobile.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasMobile.width, canvasMobile.height);
      }
    }
  }, [editMode]);

  // Initialize canvas when image loads
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>, isMobile: boolean = false) => {
    const img = e.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    
    // Resize canvas to match displayed image size using getBoundingClientRect for accuracy
    const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (canvas) {
      const rect = img.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }
    
    // If we were waiting for the edited image to load, show success now
    if (isWaitingForImageLoad) {
      setIsWaitingForImageLoad(false);
      setIsSubmitting(false);
      setShowEditSuccess(true);
      // Clear instruction and mask now that the image has loaded
      setInstruction('');
      clearMask();
      // La imagen nueva ya no tiene el objeto borrado: marcas y candidatas no aplican.
      clearBorrado();
      // Clear selected product after product_change edit completes
      if (editMode === 'product_change') {
        setSelectedProduct(null);
      }
    }
  };

  // Re-initialize canvas when editMode changes to precise / object_erase
  useEffect(() => {
    if (editMode === 'precise' || editMode === 'object_erase') {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const isMobile = window.innerWidth < 768;
        const img = isMobile ? imageMobileRef.current : imageRef.current;
        const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
        if (img && canvas) {
          // Get the actual rendered dimensions
          const rect = img.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = rect.height;
        }
      });
    }
  }, [editMode]);

  // Get mask as base64 PNG (RGBA with transparency for OpenAI)
  const getMaskBase64 = (): string | null => {
    const isMobile = window.innerWidth < 768;
    const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (!canvas || !imageDimensions) return null;
    
    // Create a new canvas at the original image dimensions
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = imageDimensions.width;
    outputCanvas.height = imageDimensions.height;
    const outputCtx = outputCanvas.getContext('2d');
    if (!outputCtx) return null;
    
    // Fill with black (areas to keep)
    outputCtx.fillStyle = 'black';
    outputCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    
    // Scale and draw the mask (white areas = areas to edit)
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    
    // Get the display canvas data and scale it to original image size
    const scaleX = imageDimensions.width / canvas.width;
    const scaleY = imageDimensions.height / canvas.height;
    
    const sourceData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Draw white where the user painted (red areas become white in mask)
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const i = (y * canvas.width + x) * 4;
        // Check if pixel has red color (user painted here)
        if (sourceData.data[i] > 100 && sourceData.data[i + 3] > 50) {
          // Draw a scaled circle on the output
          const outX = Math.floor(x * scaleX);
          const outY = Math.floor(y * scaleY);
          const radius = Math.max(1, Math.floor(20 * scaleX));
          outputCtx.fillStyle = 'white';
          outputCtx.beginPath();
          outputCtx.arc(outX, outY, radius, 0, Math.PI * 2);
          outputCtx.fill();
        }
      }
    }
    
    return outputCanvas.toDataURL('image/png').split(',')[1];
  };

  // Drawing handlers for mask canvas - use event target to get the correct canvas
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (editMode !== 'precise') return;
    setIsDrawing(true);
    draw(e);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    // Check if any mask was drawn on either canvas
    const isMobile = window.innerWidth < 768;
    const canvas = isMobile ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((val, i) => i % 4 === 3 && val > 0);
        setHasMask(hasContent);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || editMode !== 'precise') return;
    // Get canvas from event target
    const canvas = e.currentTarget;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      e.preventDefault(); // Prevent scrolling on mobile
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Scale coordinates to canvas internal size (fixes distortion)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    // Draw solid red (no alpha) - we'll handle transparency via CSS
    ctx.fillStyle = 'rgb(239, 68, 68)';
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2);
    ctx.fill();
  };

  const clearMask = () => {
    // Clear both canvases
    const canvasDesktop = maskCanvasRef.current;
    const canvasMobile = maskCanvasMobileRef.current;
    if (canvasDesktop) {
      const ctx = canvasDesktop.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasDesktop.width, canvasDesktop.height);
    }
    if (canvasMobile) {
      const ctx = canvasMobile.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasMobile.width, canvasMobile.height);
    }
    setHasMask(false);
  };

  // ───────────────────────── Modo "Borrar objeto" ─────────────────────────

  /** Radio del trazo, en píxeles de PANTALLA. El canvas se dimensiona en CSS px, así
   *  que el mismo número sirve para dibujar; para la máscara se convierte a px de la
   *  imagen real. */
  const RADIO_TRAZO = 16;

  /** Deja el modo como recién entrado: sin trazo, sin máscara y sin detección en vuelo. */
  const clearBorrado = useCallback(() => {
    maskReqIdRef.current += 1;
    trazandoRef.current = false;
    trazoRef.current = [];
    overlayRef.current = null;
    setBorradoMask(null);
    setBorradoOrigen(null);
    setBorradoStatus('idle');
    setBorradoError(null);
  }, []);

  /** El canvas activo según el breakpoint (los dos layouts viven en el DOM a la vez). */
  const canvasBorrado = (): HTMLCanvasElement | null =>
    (window.innerWidth < 768 ? maskCanvasMobileRef.current : maskCanvasRef.current);

  /**
   * Punto del evento en píxeles de la imagen REAL, o `null` si cayó fuera de la imagen.
   *
   * Se mapea contra la caja real de la imagen (`cajaContenido`) y no contra el canvas
   * entero: con `object-contain` puede quedar banda a los costados, y ahí mapear contra
   * el canvas manda a SAM coordenadas corridas.
   */
  const puntoDesdeEvento = (e: React.PointerEvent<HTMLCanvasElement>): PuntoImagen | null => {
    const canvas = e.currentTarget;
    if (!imageDimensions) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height || !canvas.width || !canvas.height) return null;
    const cx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const cy = (e.clientY - rect.top) * (canvas.height / rect.height);
    const caja = cajaContenido(canvas, imageDimensions);
    if (cx < caja.x || cx > caja.x + caja.w || cy < caja.y || cy > caja.y + caja.h) return null;
    return {
      x: ((cx - caja.x) / caja.w) * imageDimensions.width,
      y: ((cy - caja.y) / caja.h) * imageDimensions.height,
    };
  };

  /** `RADIO_TRAZO` (px de pantalla) convertido a px de la imagen real. */
  const radioTrazoEnImagen = (): number => {
    const canvas = canvasBorrado();
    if (!canvas || !imageDimensions || !canvas.width) return RADIO_TRAZO;
    const caja = cajaContenido(canvas, imageDimensions);
    return caja.w > 0 ? (RADIO_TRAZO * imageDimensions.width) / caja.w : RADIO_TRAZO;
  };

  /**
   * Manda el gesto a SAM y deja la máscara lista.
   *
   * Si SAM no encuentra nada y el gesto fue un TRAZO, se usa como máscara lo que el
   * usuario pintó: pidió borrar eso, así que borrarlo literal es mejor que devolverle un
   * error. Con un solo toque no hay nada que pintar (un punto no es un área), así que ahí
   * sí se avisa que no se detectó el objeto.
   */
  const detectarObjeto = useCallback(
    async (puntos: PuntoImagen[]) => {
      if (!imageDimensions) return;
      const reqId = ++maskReqIdRef.current;
      const previa = borradoMask;

      // La selección es ADITIVA: lo que se marca ahora se suma a lo que ya estaba. Unir
      // es más caro que asignar, así que sólo se hace si ya había algo.
      const acumular = async (nueva: string, origen: OrigenMascara) => {
        const total = previa ? await unirMascaras([previa, nueva], imageDimensions) : nueva;
        if (maskReqIdRef.current !== reqId) return;
        // Un gesto asistido sobre una selección pintada a mano (o al revés) deja una
        // máscara MIXTA, y ahí manda el eslabón más débil.
        setBorradoOrigen((prev) => {
          if (!previa || !prev) return origen;
          return prev === origen ? prev : 'mixta';
        });
        // El trazo ya está representado en la máscara: dejarlo dibujado lo mostraría dos
        // veces, y encima con la forma cruda en vez de la del objeto detectado.
        trazoRef.current = [];
        setBorradoMask(total);
        setBorradoStatus('listo');
      };

      // Sin asistencia la máscara es lo que se pintó: no hay red de por medio, así que
      // tampoco hay estado "detectando" ni forma de fallar.
      if (!seleccionAsistida) {
        const propia = maskDesdeTrazo(puntos, imageDimensions, radioTrazoEnImagen());
        if (!propia) return;
        setBorradoError(null);
        await acumular(propia, 'manual');
        return;
      }

      const frameUrl = currentIndex >= 0 ? history[currentIndex]?.url : undefined;
      if (!frameUrl) {
        setBorradoError('No se pudo preparar la imagen. Recargá la página.');
        setBorradoStatus('error');
        return;
      }
      setBorradoStatus('detectando');
      setBorradoError(null);
      try {
        const mask = await requestMascaraBorrado({ frameUrl, puntos });
        if (maskReqIdRef.current !== reqId) return; // llegó tarde: ya hay otro gesto
        await acumular(mask, 'asistida');
      } catch (err: any) {
        if (maskReqIdRef.current !== reqId) return;
        const fallback =
          puntos.length > 1 ? maskDesdeTrazo(puntos, imageDimensions, radioTrazoEnImagen()) : null;
        if (fallback) {
          // SAM no encontró nada: lo que se borra es el trazo, o sea una máscara a mano.
          await acumular(fallback, 'manual');
          return;
        }
        // Sin plan B se avisa, pero NO se pierde lo que ya estaba marcado. El trazo sí se
        // despeja: quedó marcado sin haber seleccionado nada, que es señal equivocada.
        trazoRef.current = [];
        setBorradoError(
          err?.message ||
            'No se pudo detectar el objeto. Probá marcarlo por encima, o apagá la selección asistida.',
        );
        setBorradoStatus('error');
      }
    },
    [history, currentIndex, imageDimensions, borradoMask, seleccionAsistida],
  );

  // Un gesto = un toque (1 punto) o un arrastre (N puntos). Se usan pointer events para
  // no duplicar la lógica entre mouse y touch.
  const onBorradoPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (editMode !== 'object_erase' || isSubmitting) return;
    // Mientras detecta no se acepta otro gesto: si no, el que está en vuelo se descarta
    // al llegar y el usuario pierde algo que ya había marcado. Dura ~1-2s y el pulso
    // muestra que está trabajando.
    if (borradoStatus === 'detectando') return;
    const p = puntoDesdeEvento(e);
    if (!p) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    maskReqIdRef.current += 1; // descarta la detección anterior si seguía en vuelo
    trazandoRef.current = true;
    trazoRef.current = [p];
    // La máscara previa NO se borra: el gesto nuevo se le suma. Sólo se limpia un error
    // anterior, que ya no viene al caso.
    setBorradoError(null);
    setBorradoStatus(borradoMask ? 'listo' : 'idle');
    redrawBorrado();
  };

  const onBorradoPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!trazandoRef.current) return;
    const p = puntoDesdeEvento(e);
    if (!p) return;
    trazoRef.current = [...trazoRef.current, p];
    redrawBorrado(); // imperativo: sin re-render por cada movimiento del dedo
  };

  const onBorradoPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!trazandoRef.current) return;
    trazandoRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (trazoRef.current.length === 0) return;
    void detectarObjeto(trazoRef.current);
  };

  /**
   * Pinta el estado del modo: la máscara acumulada (overlay rojo) y, encima, el gesto
   * en curso. Los dos a la vez, porque la selección es aditiva y mientras se marca algo
   * nuevo hay que seguir viendo lo que ya estaba.
   *
   * El overlay sale del cache (`overlayRef`): durante un arrastre esto corre en cada
   * `pointermove` y procesar el PNG de la máscara en cada frame no daría.
   */
  const redrawBorrado = useCallback(() => {
    const canvas = window.innerWidth < 768 ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!imageDimensions || !canvas.width || !canvas.height) return;

    const overlay = overlayRef.current;
    if (overlay && overlay.src === borradoMask && overlay.canvas.width === canvas.width) {
      ctx.drawImage(overlay.canvas, 0, 0);
    }

    const trazo = trazoRef.current;
    if (trazo.length === 0) return;
    const caja = cajaContenido(canvas, imageDimensions);
    const aCanvas = (p: PuntoImagen) => ({
      x: caja.x + (p.x / imageDimensions.width) * caja.w,
      y: caja.y + (p.y / imageDimensions.height) * caja.h,
    });
    // Mientras SAM piensa, el trazo late. Es el feedback de "estoy detectando".
    ctx.globalAlpha = borradoStatus === 'detectando' ? 0.35 + 0.4 * pulsoRef.current : 0.75;
    // Rojo (red-500), el mismo que ya usa el pincel de la edición precisa.
    ctx.strokeStyle = 'rgb(239,68,68)';
    ctx.fillStyle = 'rgb(239,68,68)';
    ctx.lineWidth = RADIO_TRAZO * 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const pts = trazo.map(aCanvas);
    if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0].x, pts[0].y, RADIO_TRAZO, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }, [borradoMask, borradoStatus, imageDimensions]);

  /**
   * Procesa la máscara acumulada a overlay rojo y lo cachea. Corre una sola vez por
   * cambio de máscara, no por frame.
   *
   * Las máscaras pueden venir en dos formatos —binaria opaca, o blanca opaca sobre fondo
   * TRANSPARENTE— y hay que aceptar los dos. Por eso "adentro de la máscara" se decide
   * con opacidad Y luminosidad: con `globalCompositeOperation='source-in'` una máscara
   * opaca teñiría la imagen ENTERA. Mismo cuidado que tiene el backend antes de
   * mandársela a Bria (ver lib/maskDilate.ts).
   */
  useEffect(() => {
    if (editMode !== 'object_erase') return;
    // Invalida cualquier procesado en vuelo: si no, el de una máscara anterior se
    // cachearía después del de la actual.
    const token = ++borradoDrawRef.current;
    if (!borradoMask || !imageDimensions) {
      overlayRef.current = null;
      redrawBorrado();
      return;
    }
    const canvas = window.innerWidth < 768 ? maskCanvasMobileRef.current : maskCanvasRef.current;
    if (!canvas || !canvas.width || !canvas.height) return;
    const caja = cajaContenido(canvas, imageDimensions);
    const img = new Image();
    img.onload = () => {
      if (borradoDrawRef.current !== token) return;
      const off = document.createElement('canvas');
      off.width = canvas.width;
      off.height = canvas.height;
      const octx = off.getContext('2d');
      if (!octx) return;
      // Sólo sobre la caja real de la imagen: fuera de ahí queda transparente.
      octx.drawImage(img, caja.x, caja.y, caja.w, caja.h);
      const data = octx.getImageData(0, 0, off.width, off.height);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const dentro = px[i + 3] > 127 && (px[i] + px[i + 1] + px[i + 2]) / 3 > 127;
        if (dentro) {
          px[i] = 239;
          px[i + 1] = 68;
          px[i + 2] = 68;
          px[i + 3] = 150;
        } else {
          px[i + 3] = 0;
        }
      }
      octx.putImageData(data, 0, 0);
      overlayRef.current = { src: borradoMask, canvas: off };
      redrawBorrado();
    };
    img.src = borradoMask;
  }, [borradoMask, imageDimensions, editMode, redrawBorrado]);

  useEffect(() => {
    if (editMode !== 'object_erase') return;
    redrawBorrado();
  }, [editMode, redrawBorrado]);

  // Pulso mientras SAM detecta. Es barato: cada frame sólo redibuja el trazo sobre el
  // overlay cacheado, sin reprocesar ningún PNG.
  useEffect(() => {
    if (borradoStatus !== 'detectando') {
      pulsoRef.current = 0;
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = () => {
      pulsoRef.current = 0.5 + 0.5 * Math.sin((performance.now() - t0) / 220);
      redrawBorrado();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [borradoStatus, redrawBorrado]);

  // Al salir del modo se limpia todo: el trazo son coordenadas de ESTA imagen.
  useEffect(() => {
    if (editMode !== 'object_erase') clearBorrado();
  }, [editMode, clearBorrado]);

  // Cambió la imagen (deshacer/rehacer, o entró una edición nueva): el trazo y la
  // máscara son de la imagen anterior y ya no aplican.
  useEffect(() => {
    clearBorrado();
  }, [history[currentIndex]?.id, clearBorrado]);

  /** `opts.omitirChequeoRegenerar` = el usuario apretó "Editar igual" en la
   *  sugerencia de regenerar; sin el flag el backend volvería a sugerir y quedaría
   *  en loop. */
  const handleSendInstruction = async (opts?: { omitirChequeoRegenerar?: boolean }) => {
    try {
      // Cada modo tiene su propia condición de "hay algo para mandar". Sin esta rama,
      // "Borrar objeto" caía en el `else` y salía SIEMPRE por instrucción vacía (no
      // tiene campo de texto): el botón no hacía nada.
      if (editMode === 'product_change') {
        if (!selectedProduct) return;
      } else if (editMode === 'object_erase') {
        if (!borradoMask) return;
      } else {
        const trimmed = instruction.trim();
        if (!trimmed) return;
      }
      
      if (!companyId) {
        setError('Falta el companyId para editar.');
        return;
      }

      // Check subscription limits before editing
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) return;

      setIsSubmitting(true);
      setError(null);
      setShowEditSuccess(false);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setError('No se pudo identificar al usuario.');
        setIsSubmitting(false);
        return;
      }

      const currentImageId = (currentIndex >= 0 && history[currentIndex]
        ? history[currentIndex].id
        : imageId);

      // Instrucción de ESTA edición: se guarda en el historial junto con la entrada
      // que produce, así las siguientes la pueden mandar como contexto.
      const instruccionActual = instruction.trim();

      // Contexto: las instrucciones que llevaron a la imagen que estamos editando
      // ahora, en orden. Se corta en `currentIndex` a propósito — si el usuario
      // deshizo, las ediciones descartadas NO están en la imagen y mandarlas sería
      // describirle al modelo algo que no ve.
      const edicionesPrevias = buildEdicionesPrevias(
        history
          .slice(0, currentIndex + 1)
          .map((h) => h.instruction ?? '')
      );

      // Build request body based on edit mode
      let requestBody: ImageEditBody;

      if (editMode === 'product_change' && selectedProduct) {
        requestBody = {
          user_id: user.id,
          imagen_id: currentImageId,
          variante_id: selectedProduct.id,
          mode: 'cambio_producto'
        };
      } else if (editMode === 'object_erase') {
        // La máscara ya viene del tamaño de la imagen full (SAM la generó sobre ella, o
        // se dibujó a esa escala), así que se manda tal cual: `postImageEdit` la sube al
        // bucket y viaja como `mask_key`, el mismo camino que usa la edición precisa.
        const elegida = borradoMask;
        if (!elegida) {
          setError('Marcá primero el objeto que querés borrar.');
          setIsSubmitting(false);
          return;
        }
        requestBody = {
          user_id: user.id,
          imagen_id: currentImageId,
          mode: 'borrar_objeto',
          mask: maskDataUriAB64(elegida),
          mask_origen: borradoOrigen ?? 'asistida',
        };
      } else {
        requestBody = {
          user_id: user.id,
          imagen_id: currentImageId,
          instruction: instruccionActual,
          mode: editMode === 'precise' ? 'edit_preciso' : 'edit_general'
        };

        // El contexto va SÓLO en la edición normal: en la precisa el cambio está
        // acotado al área marcada y el historial de pedidos globales no aplica.
        if (requestBody.mode === 'edit_general' && edicionesPrevias.length > 0) {
          requestBody.ediciones_previas = edicionesPrevias;
        }
        if (opts?.omitirChequeoRegenerar) {
          requestBody.omitir_chequeo_regenerar = true;
        }

        // Add mask for precise edit mode
        if (editMode === 'precise' && hasMask) {
          const maskB64 = getMaskBase64();
          if (maskB64) {
            requestBody.mask = maskB64;
          }
        }
      }

      // Atlas o n8n según flag (ADR-601). El 402 tira el copy de saldo.
      // modo_utilizado por MODO de edición (general/precisa/producto/ángulo)
      // para segmentar en el panel — ADR-602.
      const data = await postImageEdit(
        {
          ...requestBody,
          modo_utilizado:
            MODO_UTILIZADO_EDICION[requestBody.mode] ?? 'editar_imagen',
        },
        // Para edit_preciso: el helper sube la máscara al bucket bajo el
        // prefijo de la empresa y manda mask_key (ADR-603).
        { companyId },
      );

      // Check for moderation error (only applies to precise edit mode)
      if (data?.response === 'error_moderation') {
        setShowModerationError(true);
        setIsSubmitting(false);
        return;
      }

      // El backend cortó antes de generar porque conviene regenerar: no se cobró
      // crédito y no hay imagen nueva. La instrucción se deja escrita a propósito,
      // así "Editar igual" la reusa y el usuario puede reformularla si prefiere.
      if (data?.response === 'sugerir_regenerar') {
        setRegenerarSugerido({ parcial: data.parcial === true });
        setIsSubmitting(false);
        return;
      }

      // edited_image_id puede ser UUID (id) o file_name
      const newImageIdRaw: string | number | undefined = data?.edited_image_id ?? data?.imagen_id ?? data?.id;
      if (!newImageIdRaw) {
        throw new Error('No se recibió el ID de la imagen editada');
      }

      // Resolver la fila en DB (siempre como strings UUID/file_name)
      let newRow: { id: string; file_name: string } | null = null;
      const newIdStr = String(newImageIdRaw);
      // Intentar por id (UUID)
      const { data: idMatch } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name')
        .eq('id', newIdStr)
        .eq('id_company', companyId)
        .maybeSingle();
      if (idMatch) newRow = idMatch as any;
      // Intentar por file_name
      if (!newRow) {
        const { data: exact } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('file_name', newIdStr)
          .eq('id_company', companyId)
          .maybeSingle();
        if (exact) newRow = exact as any;
      }
      // Intentar prefijo file_name
      if (!newRow) {
        const { data: likeData } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .like('file_name', `${newIdStr}.%`)
          .eq('id_company', companyId)
          .maybeSingle();
        if (likeData) newRow = likeData as any;
      }
      if (!newRow?.file_name) {
        throw new Error('No se pudo cargar la nueva imagen editada');
      }

      const newFilePath = `${companyId}/full/${newRow.file_name}`;
      const { data: newSigned } = await supabase.storage.from('generadas').createSignedUrl(newFilePath, 3600);
      if (!newSigned?.signedUrl) {
        throw new Error('No se pudo obtener la URL firmada de la imagen editada');
      }

      // Append to history (truncate any redo states).
      // Se guarda la instrucción que produjo esta entrada — sólo la de los modos con
      // texto; el cambio de producto no aporta ninguna. Al truncar el redo, las
      // instrucciones descartadas se van con él, que es lo que queremos.
      setHistory((prev) => {
        const base = currentIndex >= 0 ? prev.slice(0, currentIndex + 1) : prev;
        return [
          ...base,
          {
            id: newRow!.id as string,
            url: newSigned.signedUrl,
            // Sólo los modos con instrucción escrita aportan contexto a las ediciones
            // siguientes. Cambio de producto y borrado no escriben nada.
            instruction:
              editMode === 'product_change' || editMode === 'object_erase'
                ? undefined
                : instruccionActual,
          },
        ];
      });
      setCurrentIndex((prev) => (prev >= 0 ? prev + 1 : 0));

      // Don't clear instruction yet - wait for the image to load
      // Don't show success yet - wait for the image to load
      setIsWaitingForImageLoad(true);
    } catch (e: any) {
      console.error('Error enviando instrucción de edición:', e);
      setError(e?.message || 'Ocurrió un error al editar la imagen.');
      setIsSubmitting(false);
    }
    // Note: isSubmitting will be set to false when the image loads (in handleImageLoad)
  };

  const handleUndo = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRedo = () => {
    if (currentIndex < history.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const persistActiveFlags = async () => {
    try {
      if (!companyId) return;
      const ids = Array.from(new Set(history.map((h) => h.id)));
      const activeId = (currentIndex >= 0 && history[currentIndex]
        ? history[currentIndex].id
        : imageId);
      if (ids.length === 0 || !activeId) return;

      // Deactivate all in session
      const { error: deactErr } = await supabase
        .from('imagenes_generadas')
        .update({ active: false })
        .in('id', ids)
        .eq('id_company', companyId);
      if (deactErr) {
        console.error('Error desactivando imágenes:', deactErr);
      }

      // Activate current
      const { error: actErr } = await supabase
        .from('imagenes_generadas')
        .update({ active: true })
        .eq('id', activeId)
        .eq('id_company', companyId);
      if (actErr) {
        console.error('Error activando la imagen actual:', actErr);
      }
    } catch (e) {
      console.error('Error guardando estados activos:', e);
    }
  };

  // Helper: navigate back to the video page when we came from ConfigurarVideoInfluencerVideo
  const navigateBackToVideo = (activeImageId: string) => {
    const st = (location.state as any) || {};
    if (!st.returnToVideo || !st.videoPageParams) return false;
    const { ids, style, companyId: vCompanyId } = st.videoPageParams;
    const qp = new URLSearchParams();
    if (ids) qp.set('ids', ids);
    if (style) qp.set('style', style);
    qp.set('imageId', activeImageId);
    if (vCompanyId) qp.set('companyId', vCompanyId);
    navigate(`/redes-sociales/crear-anuncio/configurar-video-influencer-video?${qp.toString()}`, {
      state: { editedImageId: activeImageId, savedDialogue: st.savedDialogue, savedAccent: st.savedAccent, savedDuration: st.savedDuration },
      replace: true,
    });
    return true;
  };

  // Helper: navigate back to ConfigurarVideoPersonalizado when we came from there
  const navigateBackToVideoPersonalizado = (activeImageId: string) => {
    const st = (location.state as any) || {};
    if (!st.returnToVideoPersonalizado || !st.videoPersonalizadoParams) return false;
    const { ids } = st.videoPersonalizadoParams;
    const qp = new URLSearchParams();
    if (ids) qp.set('ids', ids);
    navigate(`/redes-sociales/crear-anuncio/configurar-video-personalizado?${qp.toString()}`, {
      state: { editedImageId: activeImageId, videoConfig: st.videoConfig },
      replace: true,
    });
    return true;
  };

  const handleSaveAndExit = async () => {
    try {
      setIsSaving(true);
      // Show the same loading animation used on initial mount
      setLoading(true);
      await persistActiveFlags();
    } finally {
      setIsSaving(false);
      const st = (location.state as any) || {};
      if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
        const activeId = currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : imageId;
        const originalList = (st.resultsImageIds as any[]).map((x) => String(x));
        const sessionIds = Array.from(new Set(history.map((h) => h.id)));

        let updatedIds: string[] = originalList.slice();
        if (sessionIds.length > 0) {
          const firstSessionIdx = originalList.findIndex((id: string) => sessionIds.includes(id));
          const filtered = originalList.filter((id: string) => !sessionIds.includes(id));
          const insertAt = firstSessionIdx >= 0 ? Math.min(firstSessionIdx, filtered.length) : filtered.length;

          if (filtered.includes(activeId)) {
            filtered.splice(filtered.indexOf(activeId), 1);
          }
          filtered.splice(insertAt, 0, activeId);
          updatedIds = filtered;
        } else if (!updatedIds.includes(activeId)) {
          updatedIds = [...updatedIds, activeId];
        }

        const uniqueUpdatedIds = updatedIds.filter((id, index, arr) => arr.indexOf(id) === index);
        // Convert IDs back to numbers for GeneratedImagesInline compatibility
        const numericIds = uniqueUpdatedIds.map(id => {
          const num = Number(id);
          return isNaN(num) ? id : num; // Keep as-is if not a valid number
        });
        // Update cached images: remove old session images and add the new edited one
        let updatedCachedImages: any[] = [];
        if (Array.isArray(st.cachedImages)) {
          // Filter out images that were part of the edit session (replaced by new versions)
          updatedCachedImages = st.cachedImages.filter((img: any) => 
            !sessionIds.includes(String(img.id))
          );
          // Add the new edited image if we have a URL
          if (imageUrl && activeId) {
            // Find the original image to get fileName (or use a placeholder)
            const originalImg = st.cachedImages.find((img: any) => String(img.id) === imageId);
            // Try to convert activeId to number, keep as string if not valid
            const activeIdNum = Number(activeId);
            const finalActiveId = isNaN(activeIdNum) ? activeId : activeIdNum;
            updatedCachedImages.push({
              id: finalActiveId,
              fileName: originalImg?.fileName || '',
              signedUrl: imageUrl,
              favorito: originalImg?.favorito || false,
            });
          }
        }
        navigate('/redes-sociales/imagenes-generadas', {
          state: {
            imageIds: numericIds,
            companyId: st.resultsCompanyId,
            preloadedImages: updatedCachedImages.length > 0 ? updatedCachedImages : undefined,
          },
          replace: true,
        });
        return;
      }
      // Return to video page if we came from there
      const saveActiveId = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].id : imageId;
      if (navigateBackToVideo(saveActiveId)) return;
      if (navigateBackToVideoPersonalizado(saveActiveId)) return;
      navigate(-1);
    }
  };

  // Save as new image: keep the original active, only activate the new edited image
  const handleSaveAsNewAndExit = async () => {
    try {
      setIsSaving(true);
      setLoading(true);

      if (companyId) {
        const activeId = (currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : imageId);
        if (activeId && activeId !== imageId) {
          // Only activate the new edited image, don't deactivate the original
          const { error: actErr } = await supabase
            .from('imagenes_generadas')
            .update({ active: true })
            .eq('id', activeId)
            .eq('id_company', companyId);
          if (actErr) {
            console.error('Error activando la nueva imagen:', actErr);
          }

          // Deactivate intermediate edits (not the original and not the current)
          const intermediateIds = Array.from(new Set(history.map((h) => h.id)))
            .filter(id => id !== imageId && id !== activeId);
          if (intermediateIds.length > 0) {
            const { error: deactErr } = await supabase
              .from('imagenes_generadas')
              .update({ active: false })
              .in('id', intermediateIds)
              .eq('id_company', companyId);
            if (deactErr) {
              console.error('Error desactivando imágenes intermedias:', deactErr);
            }
          }
        }
      }
    } finally {
      setIsSaving(false);
      const st = (location.state as any) || {};
      if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
        const activeId = currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : imageId;
        const originalList = (st.resultsImageIds as any[]).map((x) => String(x));

        // Keep original in list and add the new image after it
        let updatedIds: string[] = originalList.slice();
        if (!updatedIds.includes(activeId)) {
          const originalIdx = updatedIds.indexOf(imageId);
          if (originalIdx >= 0) {
            updatedIds.splice(originalIdx + 1, 0, activeId);
          } else {
            updatedIds.push(activeId);
          }
        }

        const uniqueUpdatedIds = updatedIds.filter((id, index, arr) => arr.indexOf(id) === index);
        const numericIds = uniqueUpdatedIds.map(id => {
          const num = Number(id);
          return isNaN(num) ? id : num;
        });

        let updatedCachedImages: any[] = [];
        if (Array.isArray(st.cachedImages)) {
          updatedCachedImages = [...st.cachedImages];
          if (imageUrl && activeId) {
            const originalImg = st.cachedImages.find((img: any) => String(img.id) === imageId);
            const activeIdNum = Number(activeId);
            const finalActiveId = isNaN(activeIdNum) ? activeId : activeIdNum;
            updatedCachedImages.push({
              id: finalActiveId,
              fileName: originalImg?.fileName || '',
              signedUrl: imageUrl,
              favorito: originalImg?.favorito || false,
            });
          }
        }
        navigate('/redes-sociales/imagenes-generadas', {
          state: {
            imageIds: numericIds,
            companyId: st.resultsCompanyId,
            preloadedImages: updatedCachedImages.length > 0 ? updatedCachedImages : undefined,
          },
          replace: true,
        });
        return;
      }
      // Return to video page if we came from there
      const saveNewActiveId = currentIndex >= 0 && history[currentIndex] ? history[currentIndex].id : imageId;
      if (navigateBackToVideo(saveNewActiveId)) return;
      if (navigateBackToVideoPersonalizado(saveNewActiveId)) return;
      navigate(-1);
    }
  };


  /**
   * "Regenerar la imagen" de la sugerencia: vuelve a la grilla de resultados y la
   * dispara al llegar (`autoRegenerar`), igual que hace `autoCarrusel`. La
   * regeneración no vive acá: la corre `GeneratedImagesPage` con `lastPayload`, que
   * es la config de la generación original.
   *
   * Si no se entró desde la grilla (`returnToResults`), el editor no tiene a dónde
   * volver, así que el botón no se muestra — ver `puedeRegenerar`.
   */
  const handleRegenerarDesdeSugerencia = () => {
    const st = (location.state as any) || {};
    setRegenerarSugerido(null);
    setLoading(true);
    navigate('/redes-sociales/imagenes-generadas', {
      state: {
        imageIds: st.resultsImageIds,
        companyId: st.resultsCompanyId,
        preloadedImages: st.cachedImages,
        // La config de la generación original. Sin esto `handleGenerateMore` cae en
        // `loadLastCrearAnuncioState()`, que es la ÚLTIMA config usada y puede ser
        // de otra generación → regeneraría lo equivocado.
        lastPayload: st.lastPayload,
        autoRegenerar: true,
      },
      replace: true,
    });
  };

  /** El botón sólo tiene sentido si se llegó desde la grilla de resultados. */
  const puedeRegenerar = (() => {
    const st = (location.state as any) || {};
    return st.returnToResults === true && Array.isArray(st.resultsImageIds);
  })();

  /**
   * Controles del modo "Borrar objeto" — reemplazan el textarea, porque acá no hay nada
   * que escribir: se marca sobre la imagen. Definido una sola vez y usado en los dos
   * layouts para que el copy no se duplique, igual que `avisoRegenerar`.
   *
   * Deliberadamente MÍNIMO: sólo dice en qué estado está y, cuando hay una selección,
   * ofrece rehacerla. No hay sumar/restar ni opciones para comparar — se probaron y se
   * sacaron: marcar de nuevo es más rápido que corregir una selección a puntos.
   */
  const panelBorrado = editMode === 'object_erase' ? (
    <div className="px-4 py-3 space-y-2.5">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
          {borradoStatus === 'detectando' ? (
            <Loader2 className="w-4 h-4 text-neutral-600 animate-spin" />
          ) : (
            <Eraser className="w-4 h-4 text-neutral-600" />
          )}
        </div>
        <span
          className={`text-[15px] flex-1 min-w-0 ${
            borradoStatus === 'error' ? 'text-red-600' : 'text-neutral-600'
          }`}
        >
          {borradoStatus === 'detectando'
            ? 'Detectando el objeto...'
            : borradoStatus === 'error'
              ? borradoError
              : borradoStatus === 'listo'
                ? 'Listo para borrar. Marcá algo más para sumarlo a la selección.'
                : seleccionAsistida
                  ? 'Tocá lo que querés sacar, o pasá por encima para marcarlo'
                  : 'Pintá por encima de lo que querés sacar'}
        </span>
      </div>

      {/* Válvula de escape: si la detección no acierta en las imágenes de alguien, apagar
          esto convierte el modo en un pincel común (la máscara es lo que se pinta). */}
      <button
        type="button"
        role="switch"
        aria-checked={seleccionAsistida}
        onClick={() => setSeleccionAsistida((v) => !v)}
        disabled={isSubmitting || borradoStatus === 'detectando'}
        className="flex items-center gap-2 pl-11 disabled:opacity-50 disabled:cursor-not-allowed group"
      >
        {/* Medidas en estilos inline: son valores chicos y puntuales, y así no dependen
            de que Tailwind compile clases arbitrarias. */}
        <span
          className={`relative rounded-full transition-colors flex-shrink-0 ${
            seleccionAsistida ? 'bg-brand-primary' : 'bg-neutral-300'
          }`}
          style={{ width: 30, height: 18 }}
        >
          <span
            className="absolute rounded-full bg-white transition-all"
            style={{ width: 14, height: 14, top: 2, left: seleccionAsistida ? 14 : 2 }}
          />
        </span>
        <span className="text-[13px] text-neutral-500 group-hover:text-neutral-700 transition-colors">
          Selección asistida
        </span>
      </button>
    </div>
  ) : null;

  /**
   * "Quitar selección" vive en el footer del input, en la misma fila que el botón de
   * enviar (a la izquierda, donde en los otros modos va el badge de ayuda). Definido una
   * sola vez y usado en los dos layouts. Sólo aparece cuando hay algo que quitar.
   *
   * Dice "quitar" y no "borrar" a propósito: queda al lado del botón que SÍ borra (y
   * gasta un crédito), y dos "borrar" juntos con significados distintos es una trampa.
   * Píldora como el de enviar, para que la fila se lea pareja.
   */
  const botonQuitarSeleccion =
    editMode === 'object_erase' && (borradoStatus === 'listo' || borradoStatus === 'error') ? (
      <button
        type="button"
        onClick={clearBorrado}
        disabled={isSubmitting}
        className="py-2 px-4 bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-[13px] font-medium inline-flex items-center gap-1.5 flex-shrink-0"
      >
        <X className="w-3.5 h-3.5" />
        Quitar selección
      </button>
    ) : null;

  /**
   * Aviso de "conviene regenerar": va DENTRO del container del input, reemplazando
   * el textarea, en vez de una modal encima — la sugerencia es sobre lo que el usuario
   * acaba de escribir, así que se lee mejor en el mismo lugar donde lo escribió.
   *
   * Se define una sola vez y se usa en los dos layouts (desktop y mobile) para que el
   * copy no se duplique. La X vuelve al textarea con la instrucción intacta: el aviso
   * dice que el pedido es poco preciso, así que reformularlo tiene que ser posible.
   * Ver ADR-2026-08-12-clasificador-sugerir-regenerar.
   */
  const avisoRegenerar = regenerarSugerido ? (
    <div className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-4 h-4 text-neutral-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium text-brand-black/90">
            La instrucción es poco precisa para editar
          </p>
          <p className="text-[13px] text-neutral-600 mt-1">
            Si el producto salió totalmente diferente al que cargaste, volvé a generar la
            imagen. Y si con eso tampoco sale bien, probá cargando otras fotos del producto, en donde se vea clararamente junto con sus detalles.
          </p>
          {regenerarSugerido.parcial && (
            <p className="text-[13px] text-neutral-500 mt-1">
              Parte de lo que pediste sí se puede editar: si preferís, regenerá primero y
              después editá ese detalle.
            </p>
          )}
          <p className="text-[11px] text-neutral-500 mt-2">No te descontamos ninguna imagen.</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {puedeRegenerar && (
              <button
                type="button"
                onClick={handleRegenerarDesdeSugerencia}
                className="inline-flex items-center justify-center gap-2 py-2 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[13px] font-medium shadow-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Volver a generar
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setRegenerarSugerido(null);
                void handleSendInstruction({ omitirChequeoRegenerar: true });
              }}
              className={`inline-flex items-center justify-center py-2 px-4 rounded-md text-[13px] font-medium ${
                puedeRegenerar
                  ? 'text-neutral-600 hover:bg-neutral-100'
                  : 'bg-brand-primary hover:bg-brand-primary-hover text-white shadow-sm'
              }`}
            >
              Editar igual
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setRegenerarSugerido(null)}
          className="p-1.5 -mt-1 -mr-1 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-sm transition-colors flex-shrink-0"
          aria-label="Cerrar y reformular la instrucción"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  ) : null;

  // Cancel without saving: return to results if we came from there, preserving original list
  const handleCancelRef = useRef<() => void>(() => {});
  const handleCancel = () => {
    const st = (location.state as any) || {};
    if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
      setLoading(true);
      navigate('/redes-sociales/imagenes-generadas', {
        state: {
          imageIds: st.resultsImageIds,
          companyId: st.resultsCompanyId,
          preloadedImages: st.cachedImages,
          // Bug 1: reabrir el detalle mobile en la imagen que se estaba editando.
          openDetailImageId: st.detailImageId,
        },
        replace: true,
      });
      return;
    }
    setLoading(true);
    // Return to video page if we came from there (cancel = keep original imageId)
    if (navigateBackToVideo(imageId)) return;
    if (navigateBackToVideoPersonalizado(imageId)) return;
    /**
     * Se entró desde el VISOR del historial — ADR-2026-08-05-volver-del-editor-reabre-el-visor.
     *
     * Se vuelve con `expandImageId`, el camino que el historial ya tiene para abrirse
     * mostrando una imagen (y que ordena los estados del lightbox con el doble rAF).
     * `navigate(-1)` devolvía a la grilla con el visor cerrado: la imagen que se estaba
     * editando quedaba perdida entre todas las demás.
     */
    if (st.volverAlVisor && st.imagenId) {
      navigate('/redes-sociales/imagenes', {
        state: { expandImageId: String(st.imagenId) },
      });
      return;
    }
    navigate(-1);
  };
  handleCancelRef.current = handleCancel;

  // Override mobile back button to use handleCancel (preserves dialogue state when returning to video page)
  useEffect(() => {
    setMobileBackOverride(() => () => handleCancelRef.current());
    return () => setMobileBackOverride(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-full bg-surface-bg min-h-[100dvh] md:min-h-0">
      {/* Barra del editor (desktop) — ADR-2026-08-05-la-barra-de-editar-video-copia-la-de-canva.
          Las mismas clases que "Edición Canva" y "Editar video con IA": los tres son
          editores a pantalla completa hospedados en el shell y tienen que verse igual.
          `barra-editor` evita que `.shell-seccion h1` le imponga los 32px de un título
          de sección. */}
      {/* El marco del shell — ADR-2026-08-05-los-editores-usan-el-marco-del-shell.
          Barra y contenido viven DENTRO del mismo contenedor `fixed`: son dos capas de
          una misma superficie, y separadas (dos `fixed` hermanos) no había forma de
          redondearlas juntas ni de recortar el contenido con el radio. `overflow-hidden`
          es lo que hace que el contenido respete las esquinas. */}
      <div
        className={`hidden md:flex md:flex-col fixed right-0 bottom-0 z-40 overflow-hidden bg-surface-content ${MARCO_PANTALLA} ${BORDE_PANTALLA}`}
        style={{
          left: sidebarWidth,
          // Arranca debajo de la barra de créditos, como los paneles de modo
          // (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra).
          top: 'calc(var(--shell-barra-creditos, 0px) + 8px)',
        }}
      >
        <div className="barra-editor flex items-center gap-3 h-16 px-6 shrink-0 bg-surface-card border-b border-neutral-200">
          <button
            type="button"
            onClick={handleCancel}
            className="w-9 h-9 rounded-full flex items-center justify-center text-neutral-600 hover:bg-neutral-100 active:scale-95 transition"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-[20px] font-semibold text-brand-black/90 truncate min-w-0">
            Editar imagen
          </h1>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={handleCancel}
              className="h-9 px-2.5 rounded-md text-neutral-700 hover:bg-neutral-100 text-[13px] font-medium inline-flex items-center gap-1.5 transition"
            >
              Cancelar
            </button>
          </div>
        </div>

      {/* Contenido, ya dentro del marco: la imagen a la izquierda y el panel de
          edición a la derecha. Antes era otro `fixed` hermano de la barra. */}
      <div className="flex-1 min-h-0 flex flex-row items-start justify-center px-4 pt-4 pb-4 gap-4 overflow-hidden">
        {/* Left: image viewer — flex-shrink-0, image drives width */}
        <div className="flex-shrink-0 flex items-center justify-center relative" style={{ height: '100%' }}>
          {loading ? (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
          ) : imageUrl ? (
            <div ref={imageContainerRef} className="relative flex items-center justify-center h-full">
              {(isSubmitting || isWaitingForImageLoad || showEditSuccess) && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                    {(isSubmitting || isWaitingForImageLoad) ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                        <span className="text-[13px] font-medium text-neutral-700">Editando imagen</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-[13px] font-medium text-neutral-700">Imagen editada</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Imagen a editar"
                style={{
                  height: '100%',
                  width: 'auto',
                  // Reserva espacio: sidebar + panel derecho max (520) + gaps/padding (~64)
                  maxWidth: `calc(100vw - ${sidebarWidth}px - 584px)`,
                  // Con height fijo + width auto, un <img> nace con ancho ~0 y
                  // "salta" al decodificar. Fijando el aspect-ratio (el que llega
                  // de resultados, o el real ya medido) el ancho nace correcto →
                  // la imagen aparece en su caja final, sin salto.
                  aspectRatio: imageDimensions
                    ? `${imageDimensions.width} / ${imageDimensions.height}`
                    : initialAspect
                      ? String(initialAspect)
                      : undefined,
                }}
                className="object-contain rounded-2xl"
                onLoad={(e) => handleImageLoad(e, false)}
              />
              {/* Canvas overlay: pincel de la edición precisa y toques del borrado.
                  El borrado no dibuja trazos —cada toque es un punto y el overlay lo
                  pinta SAM—, así que va con opacidad plena (el rojo ya es translúcido)
                  y sin los handlers de arrastre. */}
              {(editMode === 'precise' || editMode === 'object_erase') && (
                <canvas
                  ref={maskCanvasRef}
                  className="absolute top-0 left-0 w-full h-full rounded-2xl cursor-crosshair"
                  style={{
                    pointerEvents: 'auto',
                    opacity: editMode === 'object_erase' ? 1 : 0.4,
                    touchAction: 'none',
                  }}
                  onPointerDown={onBorradoPointerDown}
                  onPointerMove={onBorradoPointerMove}
                  onPointerUp={onBorradoPointerUp}
                  onPointerCancel={onBorradoPointerUp}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              )}
            </div>
          ) : (
            <div className="text-neutral-500 text-[15px]">Imagen no disponible</div>
          )}
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-[440px] max-w-[520px] bg-surface-card flex flex-col rounded-2xl border border-neutral-200" style={{ height: '100%' }}>
          {/* Error banner */}
          {error && (
            <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-[13px]">{error}</div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex-1 p-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
              <h3 className="text-[17px] font-semibold text-brand-black/90">Cargando...</h3>
              <p className="text-neutral-500 mt-2 text-[15px]">Este proceso puede tardar unos momentos.</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-end p-4 overflow-hidden">
              {/* Clear mask button when in precise mode with mask */}
              {editMode === 'precise' && hasMask && (
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={clearMask}
                    disabled={isSubmitting}
                    className="h-8 px-3 bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm text-[13px] font-normal inline-flex items-center gap-2"
                  >
                    <Eraser className="w-4 h-4" />
                    Limpiar selección
                  </button>
                </div>
              )}
              {/* Mode illustration (desktop, before first edit) */}
              {history.length <= 1 && (editMode === 'instruction' || editMode === 'precise' || editMode === 'object_erase' || editMode === 'product_change') && (
                <div className="flex-1 flex flex-col items-center justify-center min-h-0 mb-3">
                  {/* "Borrar objeto" reusa la ilustración de la edición precisa: las
                      dos se explican igual (marcá sobre la imagen), y el modo preciso
                      hoy está oculto, así que el dibujo no se repite en pantalla. */}
                  <img
                    src={
                      editMode === 'instruction'
                        ? '/images/editar_prompt.png'
                        : editMode === 'precise' || editMode === 'object_erase'
                          ? '/images/editar_inline.png'
                          : '/images/editar_cambio_producto.png'
                    }
                    alt={
                      editMode === 'instruction'
                        ? 'Edición con instrucción'
                        : editMode === 'precise'
                          ? 'Edición precisa'
                          : editMode === 'object_erase'
                            ? 'Borrar objeto'
                            : 'Cambio de producto'
                    }
                    className="max-h-[55%] w-auto object-contain"
                    draggable={false}
                  />
                  <p className="text-[13px] text-neutral-500 text-center mt-3 max-w-[280px]">
                    {editMode === 'instruction'
                      ? 'Describí con texto el cambio que querés hacer en la imagen y Atlas lo aplicará.'
                      : editMode === 'precise'
                      ? 'Marcá sobre la imagen el área que querés modificar y luego describí el cambio.'
                      : editMode === 'object_erase'
                      ? 'Tocá sobre la imagen lo que querés sacar. Atlas lo detecta solo y rellena el fondo.'
                      : 'Seleccioná un producto de tu catálogo para reemplazarlo en la imagen.'}
                  </p>
                </div>
              )}

              {/* Text field container */}
              <div className={`flex flex-col bg-surface-elevated border border-neutral-200 rounded-lg ${isSubmitting ? 'shine-effect' : ''} focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-primary`}>
                <div className="relative min-h-[44px]">
                  {/* `key={editMode}` fuerza remount del contenido cuando
                      el modo cambia, y la clase `animate-mode-swap` aplica
                      un fade-in con translateY-4px sobre el nuevo
                      contenido para que la transición se sienta suave en
                      vez de cortar de golpe. */}
                  <div key={regenerarSugerido ? 'aviso-regenerar' : editMode} className="animate-mode-swap">
                  {/* El aviso de regenerar tiene prioridad: reemplaza el input hasta
                      que el usuario elija (regenerar / editar igual / cerrar). */}
                  {avisoRegenerar ? (
                    avisoRegenerar
                  ) : panelBorrado ? (
                    panelBorrado
                  ) : editMode === 'precise' && !hasMask ? (
                    <div className="flex items-center gap-3 px-4 py-3 text-neutral-600">
                      <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                        <MousePointer2 className="w-4 h-4 text-neutral-600" />
                      </div>
                      <span className="text-[15px]">Marcá en la imagen el área donde quisieras realizar el cambio</span>
                    </div>
                  ) : editMode === 'product_change' ? (
                    // Mismo patrón homogéneo que en mobile: icon+text con
                    // text-neutral-600, separados por space-y-3 (solo
                    // aplica margin cuando hay segundo hijo = preview).
                    <div className="px-4 py-3 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          <RefreshCw className="w-4 h-4 text-neutral-600" />
                        </div>
                        <span className="text-[15px] text-neutral-600">
                          {selectedProduct
                            ? 'El producto seleccionado se insertará en la imagen'
                            : 'Seleccioná el producto que querés insertar en la imagen'}
                        </span>
                      </div>
                      {/* Preview del producto sólo cuando hay uno elegido.
                          El CTA "Seleccionar" cuando no lo hay vive en el
                          bottom row del input, al lado del badge. */}
                      {selectedProduct && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setShowProductPicker(true)}
                            disabled={isSubmitting}
                            className="flex items-center gap-3 p-2 bg-surface-card border border-neutral-200 rounded-sm hover:border-neutral-300 transition-colors"
                          >
                            {productThumbUrl(selectedProduct) && (
                              <CachedImg src={productThumbUrl(selectedProduct)!} alt={selectedProduct.nombre} lazy={false} className="w-12 h-12 rounded-sm object-cover flex-shrink-0" />
                            )}
                            <span className="text-[15px] font-medium text-brand-black/90 pr-1">{selectedProduct.nombre}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedProduct(null)}
                            disabled={isSubmitting}
                            className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
                            aria-label="Quitar producto"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <textarea
                      ref={desktopInputRef}
                      value={instruction}
                      onChange={(e) => {
                        setInstruction(e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                      placeholder="Describí el cambio que querés hacer..."
                      disabled={isSubmitting}
                      rows={1}
                      className="w-full pl-4 pr-4 bg-transparent focus:outline-none border-none text-[15px] placeholder:text-neutral-400 disabled:text-neutral-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
                      style={{
                        lineHeight: '1.25rem',
                        paddingTop: '0.75rem',
                        paddingBottom: '0.5rem',
                      }}
                    />
                  )}
                  </div>
                </div>
                {/* Con el aviso de regenerar visible no hay nada que enviar: las
                    acciones viven adentro del propio aviso. */}
                <div className={`flex justify-between items-center px-2 pb-2 gap-2 ${regenerarSugerido ? 'hidden' : ''}`}>
                  {/* Badge contextual — vive adentro del container del
                      input. Antes de la primera edición es un chip
                      informativo "Se consumirá 1 imagen". Después de
                      editar al menos una vez se transforma en botón
                      clickable "¿La imagen no se modificó?" que abre el
                      help popup con consejos sobre por qué la edición
                      pudo no haber funcionado como se esperaba. */}
                  {botonQuitarSeleccion ? (
                    botonQuitarSeleccion
                  ) : hasEdits ? (
                    <button
                      type="button"
                      onClick={() => setShowHelpPopup(true)}
                      className="inline-flex items-center px-2 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[11px] font-medium text-neutral-500 whitespace-nowrap transition-colors"
                    >
                      ¿La imagen no se modificó?
                    </button>
                  ) : (
                    // Spacer: mantiene el botón enviar a la DERECHA
                    // (justify-between) cuando todavía no hay help.
                    <span aria-hidden="true" />
                  )}
                  {/* "+ Más" + popup oculto — los 4 modos ahora viven en
                      la tab bar debajo del input. Mantengo el JSX por si
                      alguna otra ruta los referencia. */}
                  <div className="relative hidden">
                    <button
                      type="button"
                      onClick={() => setShowEditModeMenu(!showEditModeMenu)}
                      disabled={isSubmitting}
                      className={`h-9 px-3 rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''} ${showEditModeMenu ? 'bg-brand-primary text-white hover:bg-brand-primary-hover border border-transparent' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-[15px]">Más</span>
                    </button>
                    {showEditModeMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowEditModeMenu(false)} />
                        <div className="absolute bottom-full left-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-3 z-10 min-w-[280px]">
                          <button
                            type="button"
                            onClick={() => { setEditMode('instruction'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left ${editMode === 'instruction' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'instruction' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Pencil className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'instruction' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Edición con instrucción</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Describí el cambio que querés hacer</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditMode('precise'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'precise' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'precise' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <MousePointer2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'precise' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Edición precisa</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Seleccioná un área específica para editar</p>
                            </div>
                          </button>
                          {/* Temporarily hidden - Borrador option
                          <button
                            type="button"
                            onClick={() => { setEditMode('eraser'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'eraser' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'eraser' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Eraser className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'eraser' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Borrador</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Eliminá elementos de la imagen</p>
                            </div>
                          </button>
                          */}
                          <button
                            type="button"
                            onClick={() => { setEditMode('product_change'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'product_change' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'product_change' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <RefreshCw className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[15px] font-medium ${editMode === 'product_change' ? 'text-brand-black/90' : 'text-brand-black/90'}`}>Cambio de producto</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Reemplazá el producto en la imagen</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {editMode === 'product_change' ? (
                    selectedProduct ? (
                      <SendCreditsButton
                        credits={1}
                        onClick={handleSendInstruction}
                        disabled={isSubmitting}
                        label="Editar"
                        ariaLabel="Editar imagen"
                      />
                    ) : (
                      // CTA "Seleccionar" cuando no hay producto elegido —
                      // mismo patrón que el mobile.
                      <button
                        type="button"
                        onClick={() => setShowProductPicker(true)}
                        disabled={isSubmitting}
                        className="h-9 px-3 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md text-[13px] font-medium shadow-sm inline-flex items-center justify-center"
                      >
                        Seleccionar
                      </button>
                    )
                  ) : editMode === 'object_erase' ? (
                    // Sin selección no hay nada que borrar; y mientras SAM resuelve el
                    // gesto, todavía no se sabe qué se va a borrar.
                    <SendCreditsButton
                      credits={1}
                      onClick={handleSendInstruction}
                      disabled={isSubmitting || borradoStatus === 'detectando' || !borradoMask}
                      label="Borrar"
                      ariaLabel="Borrar objeto"
                    />
                  ) : (
                    <SendCreditsButton
                      credits={1}
                      onClick={handleSendInstruction}
                      disabled={isSubmitting || !instruction.trim()}
                      label="Editar"
                      ariaLabel="Editar imagen"
                    />
                  )}
                </div>
              </div>

              {/* Mode tab bar (desktop) — los 3 modos como botones tipo
                  card, grid de 3 columnas (caben sin scroll en desktop).
                  Mismo estilo visual que el mobile pero sin la lógica de
                  scroll horizontal: en desktop hay espacio para mostrar
                  los 3 enteros en una fila. */}
              <div className="grid grid-cols-3 gap-2 mt-3 flex-shrink-0">
                {[
                  { mode: 'instruction' as const, label: 'Edición', Icon: Pencil },
                  // "Edición precisa" se OCULTÓ el 2026-08-13 y en su lugar entró
                  // "Borrar objeto". El modo `precise` sigue funcionando entero: para
                  // volver a mostrarlo alcanza con reponer su entrada acá y en el tab
                  // bar de mobile. Ver ADR-2026-08-13-modo-borrar-objeto.
                  { mode: 'object_erase' as const, label: 'Borrar objeto', Icon: Eraser },
                  { mode: 'product_change' as const, label: 'Cambio de producto', Icon: RefreshCw },
                ].map(({ mode, label, Icon }) => {
                  const active = editMode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setEditMode(mode)}
                      disabled={isSubmitting}
                      className={`flex flex-col items-center justify-center gap-1.5 py-2.5 px-2 rounded-md border shadow-sm transition-colors ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      } ${
                        active
                          ? 'bg-brand-primary border-brand-primary text-white'
                          : 'bg-surface-card border-neutral-200 text-neutral-600 hover:bg-surface-elevated'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[12px] font-medium leading-tight text-center">
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Botones debajo del text field - solo visibles después de al menos una edición */}
              {history.length > 1 && (
                <div className="flex items-center gap-2 mt-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={currentIndex <= 0 || isSubmitting}
                    className="flex-1 h-10 px-3 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 text-[15px]"
                    aria-label="Deshacer"
                  >
                    <Undo2 className="w-4 h-4" />
                    Deshacer
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={currentIndex >= history.length - 1 || isSubmitting}
                    className="flex-1 h-10 px-3 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 text-[15px]"
                    aria-label="Rehacer"
                  >
                    <Redo2 className="w-4 h-4" />
                    Rehacer
                  </button>
                  <div className="flex-1 relative">
                    <div className="flex h-10 rounded-md overflow-hidden shadow-sm">
                      <button
                        type="button"
                        onClick={handleSaveAndExit}
                        disabled={isSaving || !hasEdits || isSubmitting}
                        className="flex-1 h-full px-3 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-normal inline-flex items-center justify-center gap-2 rounded-l-md text-[15px]"
                      >
                        <Save className="w-4 h-4" />
                        Guardar
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSaveMenu(!showSaveMenu)}
                        disabled={isSaving || !hasEdits || isSubmitting}
                        className="h-full px-2 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white border-l border-white/20 rounded-r-md"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    {showSaveMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowSaveMenu(false)} />
                        <div className="absolute bottom-full right-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-2 z-10 w-[280px]">
                          <button
                            type="button"
                            onClick={() => { setShowSaveMenu(false); handleSaveAndExit(); }}
                            className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left"
                          >
                            <Save className="w-4 h-4 text-neutral-600" />
                            <div>
                              <p className="text-[15px] font-medium text-brand-black/90">Guardar</p>
                              <p className="text-[13px] text-neutral-500">Reemplaza la imagen original</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowSaveMenu(false); handleSaveAsNewAndExit(); }}
                            className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left mt-1"
                          >
                            <Copy className="w-4 h-4 text-neutral-600" />
                            <div>
                              <p className="text-[15px] font-medium text-brand-black/90">Guardar como nueva</p>
                              <p className="text-[13px] text-neutral-500">Conserva la imagen original</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* La info "Cada edición consume 1 imagen" se movió como
                  badge dentro del input box. El link "¿El producto se ve
                  diferente al original?" se ocultó por completo del
                  desktop (igual que en mobile) para limpiar el área
                  inferior. La lógica de `setShowHelpPopup` queda intacta
                  por si la querés volver a exponer. */}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden fixed inset-0 bg-white flex flex-col z-40 overflow-hidden px-4 pt-16">
        {/* Error banner */}
        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-[13px] flex-shrink-0">{error}</div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
            <h3 className="text-[17px] font-semibold text-brand-black/90">Cargando...</h3>
            <p className="text-neutral-500 mt-2 text-[15px]">Este proceso puede tardar unos momentos.</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-center items-center overflow-hidden min-h-0">
            {/* Imagen */}
            {imageUrl ? (
              <div className="relative flex items-center justify-center max-h-full">
                {(isSubmitting || isWaitingForImageLoad || showEditSuccess) && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-card/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm whitespace-nowrap">
                      {(isSubmitting || isWaitingForImageLoad) ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                          <span className="text-[13px] font-medium text-neutral-700">Editando imagen</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-[13px] font-medium text-neutral-700">Imagen editada</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <img
                  ref={imageMobileRef}
                  src={imageUrl}
                  alt="Imagen a editar"
                  // En mobile no se puede reservar la caja "contain" antes de
                  // cargar (no sabemos el área). La ocultamos (opacity 0) hasta
                  // que decodifica y la revelamos con un fade → el pop de tamaño
                  // del primer render ocurre invisible, no se ve el saltito.
                  style={{ opacity: imageDimensions ? 1 : 0 }}
                  className="max-w-full max-h-full object-contain rounded-md transition-opacity duration-200"
                  onLoad={(e) => handleImageLoad(e, true)}
                />
                {/* Canvas overlay (mobile) — ver el comentario del de desktop. */}
                {(editMode === 'precise' || editMode === 'object_erase') && (
                  <canvas
                    ref={maskCanvasMobileRef}
                    className="absolute top-0 left-0 w-full h-full rounded-md cursor-crosshair"
                    style={{
                      pointerEvents: 'auto',
                      opacity: editMode === 'object_erase' ? 1 : 0.4,
                      touchAction: 'none',
                    }}
                    onPointerDown={onBorradoPointerDown}
                    onPointerMove={onBorradoPointerMove}
                    onPointerUp={onBorradoPointerUp}
                    onPointerCancel={onBorradoPointerUp}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                )}
              </div>
            ) : (
              <div className="text-neutral-500 text-[15px]">Imagen no disponible</div>
            )}
          </div>
        )}

        {/* Controles (mobile) */}
        <div
          className="bg-white pt-2 pb-3 space-y-2 flex-shrink-0 -mx-4 px-4"
        >
          {/* Clear mask button when in precise mode with mask (mobile) */}
          {editMode === 'precise' && hasMask && (
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={clearMask}
                disabled={isSubmitting}
                className="h-8 px-3 bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm text-[13px] font-normal inline-flex items-center gap-2"
              >
                <Eraser className="w-4 h-4" />
                Limpiar selección
              </button>
            </div>
          )}
          <div className={`flex flex-col bg-white border border-neutral-200 rounded-lg ${isSubmitting ? 'shine-effect' : ''} focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-primary`}>
            <div className="relative min-h-[44px]">
              {/* `key={editMode}` + `animate-mode-swap` para que el cambio
                  de modo no sea abrupto: fade + translateY-4px de 200ms
                  sobre el nuevo contenido. Mismo patrón en desktop. */}
              <div key={regenerarSugerido ? 'aviso-regenerar' : editMode} className="animate-mode-swap">
              {/* Igual que en desktop: el aviso de regenerar reemplaza el input. */}
              {avisoRegenerar ? (
                avisoRegenerar
              ) : panelBorrado ? (
                panelBorrado
              ) : editMode === 'precise' && !hasMask ? (
                <div className="flex items-center gap-3 px-4 py-3 text-neutral-600">
                  <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Hand className="w-4 h-4 text-neutral-600" />
                  </div>
                  <span className="text-[15px]">Marcá en la imagen el área donde quisieras realizar el cambio</span>
                </div>
              ) : editMode === 'product_change' ? (
                // Patrón homogeneizado con Precisa: mismo padding
                // (px-4 py-3), mismo gap-3, mismo items-center entre icono
                // y texto. Cuando hay producto seleccionado se agrega un
                // bloque debajo con la preview. El `space-y-3` separa el
                // row de texto de la preview SOLO cuando ambos existen —
                // antes había un `mb-3` permanente que agregaba padding
                // bottom incluso sin preview, rompiendo la homogeneidad.
                <div className="px-4 py-3 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-sm bg-neutral-100 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-4 h-4 text-neutral-600" />
                    </div>
                    <span className="text-[15px] text-neutral-600">
                      {selectedProduct
                        ? 'El producto seleccionado se insertará en la imagen'
                        : 'Seleccioná el producto que querés insertar en la imagen'}
                    </span>
                  </div>
                  {/* Cuando hay producto seleccionado mostramos su preview
                      (imagen + nombre + botón quitar). Cuando NO hay,
                      el CTA "Seleccionar" vive en el bottom row del input
                      (a la derecha del badge), ya no acá adentro. */}
                  {selectedProduct && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowProductPicker(true)}
                        disabled={isSubmitting}
                        className="flex items-center gap-3 p-2 bg-surface-card border border-neutral-200 rounded-sm hover:border-neutral-300 transition-colors"
                      >
                        {productThumbUrl(selectedProduct) && (
                          <CachedImg src={productThumbUrl(selectedProduct)!} alt={selectedProduct.nombre} lazy={false} className="w-12 h-12 rounded-sm object-cover flex-shrink-0" />
                        )}
                        <span className="text-[15px] font-medium text-brand-black/90 pr-1">{selectedProduct.nombre}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedProduct(null)}
                        disabled={isSubmitting}
                        className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-sm transition-colors"
                        aria-label="Quitar producto"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <textarea
                  value={instruction}
                  onChange={(e) => {
                    setInstruction(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  placeholder="Describí el cambio que querés hacer..."
                  disabled={isSubmitting}
                  rows={1}
                  className="w-full pl-4 pr-4 bg-transparent focus:outline-none border-none text-[15px] placeholder:text-neutral-400 disabled:text-neutral-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
                  style={{
                    lineHeight: '1.25rem',
                    paddingTop: '0.75rem',
                    paddingBottom: '0.5rem',
                  }}
                />
              )}
              </div>
            </div>
            {/* Con el aviso de regenerar visible las acciones viven adentro del aviso. */}
            <div className={`flex justify-between items-center px-2 pb-2 gap-2 ${regenerarSugerido ? 'hidden' : ''}`}>
              {/* Mobile: el "+ Más" antes vivía acá pero los 4 modos ahora
                  son una bottom tab bar al fondo de la pantalla (ver
                  abajo). Mantengo la lógica intacta para desktop. Cuando
                  está oculto el flex-justify-between deja al badge "1
                  imagen por edición" como primer elemento visible y el
                  send button como segundo, separados en los extremos. */}
              <div className="relative hidden mobile-edit-mode-popup">
                <button
                  type="button"
                  onClick={() => setShowEditModeMenu(!showEditModeMenu)}
                  disabled={isSubmitting}
                  className={`h-9 px-3 rounded-md font-normal shadow-sm inline-flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''} ${showEditModeMenu ? 'bg-brand-primary text-white hover:bg-brand-primary-hover border border-transparent' : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'}`}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[15px]">Más</span>
                </button>
                    {showEditModeMenu && (
                      <>
                        <div className="fixed inset-0 z-[5]" onClick={() => setShowEditModeMenu(false)} />
                        <div className="absolute bottom-full left-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-3 z-10 min-w-[280px]">
                          <button
                            type="button"
                            onClick={() => { setEditMode('instruction'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left ${editMode === 'instruction' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'instruction' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Pencil className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Edición con instrucción</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Describí el cambio que querés hacer</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditMode('precise'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'precise' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'precise' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <MousePointer2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Edición precisa</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Seleccioná un área específica para editar</p>
                            </div>
                          </button>
                          {/* Temporarily hidden - Borrador option
                          <button
                            type="button"
                            onClick={() => { setEditMode('eraser'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'eraser' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'eraser' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <Eraser className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Borrador</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Eliminá elementos de la imagen</p>
                            </div>
                          </button>
                          */}
                          <button
                            type="button"
                            onClick={() => { setEditMode('product_change'); setShowEditModeMenu(false); }}
                            className={`flex items-start gap-3 w-full p-3 rounded-sm transition-colors text-left mt-1 ${editMode === 'product_change' ? 'bg-neutral-100' : 'hover:bg-surface-elevated'}`}
                          >
                            <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 ${editMode === 'product_change' ? 'bg-brand-primary text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                              <RefreshCw className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/90">Cambio de producto</p>
                              <p className="text-[13px] text-neutral-500 mt-0.5">Reemplazá el producto en la imagen</p>
                            </div>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Badge contextual — chip informativo "Se consumirá 1
                      imagen" antes de la primera edición; se transforma
                      en botón "¿La imagen no se modificó?" después,
                      abriendo el help popup con consejos de troubleshoot. */}
                  {botonQuitarSeleccion ? (
                    botonQuitarSeleccion
                  ) : hasEdits ? (
                    <button
                      type="button"
                      onClick={() => setShowHelpPopup(true)}
                      className="inline-flex items-center px-2 py-1 rounded-full bg-neutral-100 active:bg-neutral-200 text-[11px] font-medium text-neutral-500 whitespace-nowrap transition-colors"
                    >
                      ¿La imagen no se modificó?
                    </button>
                  ) : (
                    // Spacer: mantiene el botón enviar a la DERECHA
                    // (justify-between) cuando todavía no hay help.
                    <span aria-hidden="true" />
                  )}
                  {editMode === 'product_change' ? (
                    selectedProduct ? (
                      <SendCreditsButton
                        credits={1}
                        onClick={handleSendInstruction}
                        disabled={isSubmitting}
                        label="Editar"
                        ariaLabel="Editar imagen"
                      />
                    ) : (
                      // CTA "Seleccionar" cuando todavía no hay producto
                      // elegido — ocupa el slot del send button para que el
                      // layout (badge izq · CTA der) sea consistente con
                      // los demás modos.
                      <button
                        type="button"
                        onClick={() => setShowProductPicker(true)}
                        disabled={isSubmitting}
                        className="h-9 px-3 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white rounded-md text-[13px] font-medium shadow-sm inline-flex items-center justify-center"
                      >
                        Seleccionar
                      </button>
                    )
                  ) : editMode === 'object_erase' ? (
                    // Sin selección no hay nada que borrar; y mientras SAM resuelve el
                    // gesto, todavía no se sabe qué se va a borrar.
                    <SendCreditsButton
                      credits={1}
                      onClick={handleSendInstruction}
                      disabled={isSubmitting || borradoStatus === 'detectando' || !borradoMask}
                      label="Borrar"
                      ariaLabel="Borrar objeto"
                    />
                  ) : (
                    <SendCreditsButton
                      credits={1}
                      onClick={handleSendInstruction}
                      disabled={isSubmitting || !instruction.trim()}
                      label="Editar"
                      ariaLabel="Editar imagen"
                    />
                  )}
                </div>
              </div>
              {/* Los botones de Deshacer / Guardar (solo visibles tras
                  al menos una edición) se movieron DEBAJO del tab bar
                  para no quedar separados de la imagen y mantener el
                  área cerca del input limpia. Ahora viven en el bloque
                  bottom de la pantalla, después de los modos. */}

              {/* "Cada edición consume 1 imagen" se movió como badge
                  dentro del container del input, y el link de ayuda
                  ("¿El producto se ve diferente al original?") se ocultó
                  por completo del mobile para limpiar el espacio debajo
                  del input. */}
            </div>

        {/* Bottom tab bar (mobile) — patrón clásico de apps de edición
            (Canva, Lightroom, etc.). Cada tab es un modo de edición; el
            activo se resalta en azul Atlas. Reemplaza el menú popup "+ Más"
            que vivía dentro del input. La safe-area-inset-bottom ahora la
            posee este contenedor para que en iPhones con notch / home
            indicator los íconos no queden tapados. */}
        <div
          className="flex-shrink-0 -mx-4 bg-white"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {/* Los 3 modos reparten el ancho en partes iguales (`flex-1`).
              Hasta que se quitó "Cambiar ángulo" esto era un scroll
              horizontal con snap y cada tab al 28% del ancho: con 4 tabs
              entraban 3 enteros + uno cortado a la derecha, y el corte era
              el affordance de "hay más para deslizar". Con 3 no hay nada
              que deslizar, así que el scroll sobraba y dejaba un hueco a la
              derecha. Cada tab conserva su border + bg para que se lea como
              botón/card individual, consistente con el resto del proyecto. */}
          <div className="flex gap-2 px-4 py-2">
            {[
              { mode: 'instruction' as const, label: 'Edición', Icon: Pencil },
              // Ver el comentario del tab bar de desktop: "Edición precisa" está
              // oculta, no borrada.
              { mode: 'object_erase' as const, label: 'Borrar objeto', Icon: Eraser },
              { mode: 'product_change' as const, label: 'Cambio de producto', Icon: RefreshCw },
            ].map(({ mode, label, Icon }) => {
              const active = editMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setEditMode(mode)}
                  disabled={isSubmitting}
                  className={`flex flex-col items-center justify-center gap-1.5 py-2.5 px-2 flex-1 min-w-0 rounded-md border shadow-sm transition-colors ${
                    isSubmitting ? 'opacity-50' : ''
                  } ${
                    active
                      ? 'bg-brand-primary border-brand-primary text-white'
                      : 'bg-surface-card border-neutral-200 text-neutral-600 active:bg-surface-elevated'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10.5px] font-medium leading-tight text-center">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Undo / Redo / Guardar — solo visibles tras al menos una
              edición. Ahora viven DEBAJO del tab bar de modos así no
              empujan al input ni se mezclan con el área de escritura.
              Comparten el mismo container con el tab bar para que el
              safe-area-inset-bottom envuelva ambos. */}
          {history.length > 1 && (() => {
            const canUndo = currentIndex > 0;
            const canRedo = currentIndex < history.length - 1;
            const bothVisible = canUndo && canRedo;
            return (
              <div className="flex gap-2 px-4 pt-1 pb-3">
                {canUndo && (
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={isSubmitting}
                    className={`h-10 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm inline-flex items-center justify-center ${bothVisible ? 'w-10' : 'px-3 gap-2'}`}
                    aria-label="Deshacer"
                  >
                    <Undo2 className="w-4 h-4" />
                    {!bothVisible && <span className="text-[15px]">Deshacer</span>}
                  </button>
                )}
                {canRedo && (
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={isSubmitting}
                    className={`h-10 bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm inline-flex items-center justify-center ${bothVisible ? 'w-10' : 'px-3 gap-2'}`}
                    aria-label="Rehacer"
                  >
                    <Redo2 className="w-4 h-4" />
                    {!bothVisible && <span className="text-[15px]">Rehacer</span>}
                  </button>
                )}
                <div className="relative flex-1">
                  <div className="flex h-10 rounded-md overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={handleSaveAndExit}
                      disabled={isSaving || !hasEdits || isSubmitting}
                      className="flex-1 h-full px-3 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-normal inline-flex items-center justify-center gap-2 rounded-l-md text-[15px]"
                    >
                      <Save className="w-4 h-4" />
                      <span className="text-[15px]">Guardar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSaveMenu(!showSaveMenu)}
                      disabled={isSaving || !hasEdits || isSubmitting}
                      className="h-full px-2 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white border-l border-white/20 rounded-r-md"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  {showSaveMenu && (
                    <>
                      <div className="fixed inset-0 z-[5]" onClick={() => setShowSaveMenu(false)} />
                      <div className="absolute bottom-full right-0 mb-2 bg-surface-card rounded-[14px] border border-neutral-200 shadow-lg p-2 z-10 w-[220px]">
                        <button
                          type="button"
                          onClick={() => { setShowSaveMenu(false); handleSaveAndExit(); }}
                          className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left"
                        >
                          <Save className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                          <div>
                            <p className="text-[15px] font-medium text-brand-black/90">Guardar</p>
                            <p className="text-[13px] text-neutral-500">Reemplaza la original</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowSaveMenu(false); handleSaveAsNewAndExit(); }}
                          className="flex items-center gap-3 w-full p-2.5 rounded-sm hover:bg-surface-elevated text-left mt-1"
                        >
                          <Copy className="w-4 h-4 text-neutral-600 flex-shrink-0" />
                          <div>
                            <p className="text-[15px] font-medium text-brand-black/90">Guardar como nueva</p>
                            <p className="text-[13px] text-neutral-500">Conserva la original</p>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Product Picker Modal
          Desktop: card centrado con backdrop oscuro (patrón anterior).
          Mobile: bottom sheet slide-up con drag handle y swipe-down-to-close
          (mismo patrón que los menús de PruebaNuevaInterfaz). El backdrop
          y el sheet hacen fade/slide juntos vía `productPickerAnimated`. */}
      {showProductPicker && (
        <>
          {/* Desktop centered modal */}
          <div className="hidden sm:flex fixed inset-0 z-50 items-center justify-center bg-brand-black/30 p-4">
            <div className="bg-surface-card rounded-md w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-lg">
              <div className="flex items-center justify-between p-4 border-b border-neutral-200">
                <h3 className="text-[17px] font-semibold text-brand-black/90">Seleccionar producto</h3>
                <button
                  type="button"
                  onClick={closeProductPicker}
                  className="p-2 text-neutral-500 hover:text-neutral-700 rounded-full hover:bg-neutral-100"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {loadingProducts ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="bg-surface-card border border-neutral-200 rounded-md overflow-hidden">
                        <div className="w-full aspect-square bg-neutral-100 animate-pulse" />
                        <div className="p-3">
                          <div className="h-4 bg-neutral-100 rounded w-2/3 animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                    <p className="text-neutral-600 text-[15px]">No tenés productos cargados.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {products.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => {
                          setSelectedProduct(product);
                          closeProductPicker();
                        }}
                        className={`relative text-left bg-surface-card border rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary group transition-all ${
                          selectedProduct?.id === product.id ? 'ring-2 ring-brand-primary border-brand-primary' : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <ProductPickerThumb
                          variantId={product.id}
                          imageKey={product.imageKey}
                          nombre={product.nombre}
                          thumb={productThumbs[product.id]}
                          onNeedsUrl={ensureProductThumb}
                        >
                          {selectedProduct?.id === product.id && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-brand-primary rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </ProductPickerThumb>
                        <div className="p-3">
                          <p className="text-[15px] text-brand-black/90 truncate font-medium">{product.nombre}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile slide-up bottom sheet */}
          <div
            className={`sm:hidden fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${productPickerAnimated ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeProductPicker}
          />
          <div
            className="sm:hidden fixed inset-x-0 bottom-0 z-[51] bg-white rounded-t-[13px] flex flex-col shadow-2xl"
            style={{
              height: 'calc(100dvh - 4rem)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
              transform:
                productPickerDragY > 0
                  ? `translateY(${productPickerDragY}px)`
                  : productPickerAnimated
                    ? 'translateY(0)'
                    : 'translateY(100%)',
              transition: productPickerDragY === 0 ? 'transform 0.3s ease-out' : 'none',
            }}
          >
            {/* Drag handle area — captura swipe-down */}
            <div
              className="py-2 cursor-grab active:cursor-grabbing touch-none"
              onTouchStart={handleProductPickerTouchStart}
              onTouchMove={handleProductPickerTouchMove}
              onTouchEnd={handleProductPickerTouchEnd}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            {/* Header: Cancelar a la izq, título centrado, X a la derecha
                — sigue el patrón iOS / PruebaNuevaInterfaz. */}
            <div className="flex items-center justify-between px-4 pb-3">
              <button
                type="button"
                onClick={closeProductPicker}
                className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left"
              >
                Cancelar
              </button>
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Seleccionar producto
              </h3>
              <div className="w-20" />
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {loadingProducts ? (
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-surface-card border border-neutral-200 rounded-md overflow-hidden">
                      <div className="w-full aspect-square bg-neutral-100 animate-pulse" />
                      <div className="p-3">
                        <div className="h-4 bg-neutral-100 rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                  <p className="text-neutral-600 text-[15px]">No tenés productos cargados.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => {
                        setSelectedProduct(product);
                        closeProductPicker();
                      }}
                      className={`relative text-left bg-white border rounded-md overflow-hidden focus:outline-none transition-all ${
                        selectedProduct?.id === product.id ? 'ring-2 ring-brand-primary border-brand-primary' : 'border-neutral-200 active:border-neutral-300'
                      }`}
                    >
                      <ProductPickerThumb
                        variantId={product.id}
                        imageKey={product.imageKey}
                        nombre={product.nombre}
                        thumb={productThumbs[product.id]}
                        onNeedsUrl={ensureProductThumb}
                      >
                        {selectedProduct?.id === product.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-brand-primary rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </ProductPickerThumb>
                      <div className="p-3">
                        <p className="text-[15px] text-brand-black/90 truncate font-medium">{product.nombre}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Moderation error message */}
      {showModerationError && (
        <div className="fixed inset-0 bg-brand-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card w-full max-w-sm rounded-md shadow-lg p-6 relative text-center">
            <button
              type="button"
              onClick={() => setShowModerationError(false)}
              className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-neutral-700 rounded-sm focus:outline-none"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">No se puede utilizar la función de "Edición precisa" con este tipo de productos. Lo sentimos.</h3>
            <button
              type="button"
              onClick={() => setShowModerationError(false)}
              className="mt-4 inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-normal shadow-sm"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Help popup */}
      {showHelpPopup && (
        <div className="fixed inset-0 bg-brand-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card w-full max-w-md rounded-md shadow-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => setShowHelpPopup(false)}
              className="absolute top-3 right-3 p-2 text-neutral-500 hover:text-neutral-700 rounded-sm focus:outline-none"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-4">
              {hasEdits ? '¿No se modificó la foto que editaste?' : '¿El producto se ve diferente al original?'}
            </h3>
            {hasEdits ? (
              <div className="text-[15px] text-neutral-600 space-y-4">
                <p>Verificá primero que la instrucción que le estás dando a Atlas no sea muy genérica. Por ejemplo, instrucciones como "Arreglá el producto" o "Que se vea mejor" no serán entendidas por la IA. Intentá usar instrucciones puntuales como: "Que el abrigo sea más largo, hasta la altura de las rodillas del modelo" o "Eliminá el bolsillo superior ubicado en la parte delantera del abrigo".</p>
                <p>También probá usar los modos de Edición Precisa y Cambiar Producto en los casos en que creas que los necesitás.</p>
                <p>Si aplicaste estas mejoras y, de igual forma, no se modificó lo que editaste, tené en cuenta que, si intentaste modificar detalles muy específicos del producto o imagen, es posible que Atlas no pueda hacerlo todavía.</p>
                <p>La IA no es perfecta, pero trabajamos día a día para mejorar. En estos casos, es mejor intentar generar una nueva imagen aclarando los detalles en la escena o subiendo una mejor foto del producto.</p>
              </div>
            ) : (
              <div className="text-[15px] text-neutral-600 space-y-4">
                <p>Si en la imagen generada el producto se ve deformado o distinto a como debería ser, antes de editar, verificá que en la foto que has subido se vean bien todos los detalles.</p>
                {userRubro === 'Moda e Indumentaria' && (
                  <p>Atlas entiende mejor tu producto cuando subís una foto del mismo en contexto. Es decir, si es una prenda, subí una foto de una persona o maniquí usando el producto, pero SIN que se vea la cara de la persona.</p>
                )}
                <p>Atlas ve en la imagen lo que vos ves. Esto quiere decir que si alguna parte del producto no es visible en la foto que subiste, Atlas no sabrá cómo es esa parte del producto.</p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowHelpPopup(false)}
              className="mt-6 inline-flex items-center justify-center w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-normal shadow-sm"
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        type={subscriptionLimitType}
        onClose={() => setShowSubscriptionLimitModal(false)}
      />
    </div>
  );
};

export default EditarImagen;
